import { useState, useEffect, useRef, useCallback } from "react";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const LANGUAGES = [
  { code: "hi-IN", label: "Hindi" }, { code: "ta-IN", label: "Tamil" },
  { code: "te-IN", label: "Telugu" }, { code: "kn-IN", label: "Kannada" },
  { code: "ml-IN", label: "Malayalam" }, { code: "mr-IN", label: "Marathi" },
  { code: "bn-IN", label: "Bengali" }, { code: "gu-IN", label: "Gujarati" },
  { code: "pa-IN", label: "Punjabi" }, { code: "ur-PK", label: "Urdu" },
  { code: "en-US", label: "English" }, { code: "es-ES", label: "Spanish" },
  { code: "fr-FR", label: "French" }, { code: "it-IT", label: "Italian" },
  { code: "zh-CN", label: "Mandarin" }, { code: "ar-SA", label: "Arabic" },
  { code: "pt-BR", label: "Portuguese" }, { code: "ru-RU", label: "Russian" },
  { code: "ja-JP", label: "Japanese" }, { code: "ko-KR", label: "Korean" },
];

const DIETARY = [
  { id: "all",            label: "All",          emoji: "🍽️", color: "#7A5840" },
  { id: "jain",           label: "Jain",         emoji: "🕊️", color: "#5A7A3A" },
  { id: "vegetarian",     label: "Vegetarian",   emoji: "🌿", color: "#3D7A55" },
  { id: "vegan",          label: "Vegan",        emoji: "🌱", color: "#2D6E3A" },
  { id: "gluten-free",    label: "Gluten-Free",  emoji: "🌾", color: "#7A5020" },
  { id: "dairy-free",     label: "Dairy-Free",   emoji: "🥛", color: "#4A7080" },
];

const OCCASIONS = [
  { id: "everyday", label: "Everyday", emoji: "🍽️" }, { id: "diwali", label: "Diwali", emoji: "🪔" },
  { id: "eid", label: "Eid", emoji: "🌙" }, { id: "christmas", label: "Christmas", emoji: "🎄" },
  { id: "holi", label: "Holi", emoji: "🎨" }, { id: "navratri", label: "Navratri", emoji: "💃" },
  { id: "wedding", label: "Wedding", emoji: "💍" }, { id: "birthday", label: "Birthday", emoji: "🎂" },
  { id: "monsoon", label: "Monsoon Special", emoji: "🌧️" }, { id: "winter", label: "Winter Special", emoji: "❄️" },
  { id: "summer", label: "Summer Special", emoji: "☀️" }, { id: "fasting", label: "Fasting", emoji: "🙏" },
];

const CUISINES = [
  { id: "gujarati", label: "Gujarati", emoji: "🥘" }, { id: "punjabi", label: "Punjabi", emoji: "🌾" },
  { id: "south-indian", label: "South Indian", emoji: "🥥" }, { id: "bengali", label: "Bengali", emoji: "🐟" },
  { id: "maharashtrian", label: "Maharashtrian", emoji: "🫓" }, { id: "rajasthani", label: "Rajasthani", emoji: "🏜️" },
  { id: "mughlai", label: "Mughlai", emoji: "🍛" }, { id: "goan", label: "Goan", emoji: "🌊" },
  { id: "kashmiri", label: "Kashmiri", emoji: "❄️" }, { id: "bihari", label: "Bihari", emoji: "🌱" },
  { id: "odia", label: "Odia", emoji: "🌺" }, { id: "assamese", label: "Assamese", emoji: "🍵" },
  { id: "hyderabadi", label: "Hyderabadi", emoji: "🍖" }, { id: "italian", label: "Italian", emoji: "🍝" },
  { id: "chinese", label: "Chinese", emoji: "🥡" }, { id: "mexican", label: "Mexican", emoji: "🌮" },
  { id: "middle-eastern", label: "Middle Eastern", emoji: "🧆" }, { id: "thai", label: "Thai", emoji: "🍜" },
  { id: "continental", label: "Continental", emoji: "🥗" }, { id: "other", label: "Other", emoji: "🌍" },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MEALS = ["Breakfast", "Lunch", "Dinner", "Snack"];

// Approximate months for occasion reminders (1-based)
const OCCASION_MONTHS = {
  diwali:[10,11], eid:[3,4,9,10], christmas:[12], holi:[3],
  navratri:[3,4,9,10], wedding:[11,12,1,2], monsoon:[6,7,8,9],
  winter:[11,12,1,2], summer:[4,5,6],
};

// ─── ACCESS CONTROL ───────────────────────────────────────────────────────────
// 🔑 Change this PIN to something only you know before deploying
const OWNER_PIN = "vault2024";

function loadIdentity() {
  try { return JSON.parse(localStorage.getItem("vault-identity") || "null"); } catch { return null; }
}
function saveIdentity(identity) { localStorage.setItem("vault-identity", JSON.stringify(identity)); }
function clearIdentity() { localStorage.removeItem("vault-identity"); }

// Permissions
const canDelete   = (identity) => identity?.isOwner === true;
const canEdit     = (identity, recipe) => identity?.isOwner === true || (identity?.name && recipe?.contributor && identity.name.trim().toLowerCase() === recipe.contributor.trim().toLowerCase());
// Everyone can comment, favourite, share, adapt

const C = {
  // Dastarkhwan palette — aged ivory body, forest green header, burnished gold accents
  bg:        "#F9F3E8",   // aged ivory page
  parchment: "#F0EAD8",   // slightly darker ivory for stats bar / meta boxes
  card:      "#FFFDF7",   // near-white cards
  brown:     "#1C3A2A",   // deep forest (header, headings)
  amber:     "#B5873A",   // burnished gold (primary accent)
  sienna:    "#8A6020",   // darker gold (button gradient end)
  text:      "#1C1208",   // near-black body text
  muted:     "#6B5B40",   // warm taupe for secondary text
  border:    "#D4C4A0",   // parchment border
  green:     "#2D6A4F",   // forest green (success, tags)
  red:       "#8B2222",   // error / delete
  overlay:   "rgba(15,25,18,0.70)",  // green-tinted dark overlay
  tag:       "#E8DFC8",   // tag background
  favourite: "#C05050",   // favourite heart
  blue:      "#2D5580",   // cuisine tag accent
  headerBg:  "linear-gradient(150deg, #1C3A2A 0%, #2D6A4F 55%, #1A3525 100%)",
  headerPattern: "repeating-linear-gradient(45deg,#B5873A 0px,#B5873A 1px,transparent 0px,transparent 50%)",
};

// ─── STYLES ───────────────────────────────────────────────────────────────────
const injectFonts = () => {
  if (document.getElementById("vault-fonts")) return;
  const l = document.createElement("link");
  l.id = "vault-fonts"; l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;0,900;1,400;1,700&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;0,700;1,300;1,400;1,600&display=swap";
  document.head.appendChild(l);
  const s = document.createElement("style");
  s.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes pulse { 0%,100%{box-shadow:0 4px 20px rgba(139,34,34,.45)}50%{box-shadow:0 4px 36px rgba(139,34,34,.75)} }
    @keyframes fadeIn { from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)} }
    * { box-sizing: border-box; }
    ::-webkit-scrollbar{width:6px}::-webkit-scrollbar-track{background:${C.parchment}}
    ::-webkit-scrollbar-thumb{background:${C.border};border-radius:3px}
    audio{accent-color:${C.amber}}
    details>summary{list-style:none}details>summary::-webkit-details-marker{display:none}
    .meal-cell:hover{background:${C.parchment}!important;cursor:pointer}
  `;
  document.head.appendChild(s);
};

const IS = { width:"100%",padding:"9px 13px",fontFamily:"Cormorant Garamond, serif",fontSize:16,color:C.text,background:C.card,border:`1.5px solid ${C.border}`,borderRadius:8,lineHeight:1.5,outline:"none" };
const BP = { width:"100%",padding:"12px 0",background:`linear-gradient(135deg,${C.green},${C.sienna})`,color:"#fff",border:"none",borderRadius:9,fontFamily:"Playfair Display, serif",fontSize:16,cursor:"pointer",letterSpacing:.3 };
const SH = { fontFamily:"Playfair Display, serif",fontSize:19,color:C.brown,margin:"0 0 14px",borderBottom:`2px solid ${C.amber}`,paddingBottom:6 };
const MB = { background:C.parchment,border:`1px solid ${C.border}`,borderRadius:9,padding:"8px 16px",textAlign:"center" };
const ML = { fontFamily:"Cormorant Garamond, serif",fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:.8 };
const MV = { fontFamily:"Playfair Display, serif",fontSize:16,color:C.brown,marginTop:2 };
const LB = (extra={}) => ({ fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,textTransform:"uppercase",letterSpacing:.8,display:"block",marginBottom:5,...extra });

// ─── AI HELPERS ───────────────────────────────────────────────────────────────
const SYSTEM = `You are a family recipe preservation assistant. Structure raw recipe text into a clean recipe card.
Preserve authentic measurements like "a fistful", "to taste". Do NOT invent ingredients or steps.
Return ONLY valid JSON, no markdown:
{"name":"","description":"warm 1-sentence evocative description","ingredients":["qty + item"],"steps":["full step"],"servings":"","cookTime":"","prepTime":"","tags":["tag1"],"notes":"family story or tip","dietaryType":"jain|vegetarian|vegan|non-vegetarian|eggetarian|gluten-free|dairy-free"}`;

async function callAI(payload) {
  const r = await fetch("/api/chat", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(payload) });
  const d = await r.json();
  if (!r.ok) throw new Error("API error: " + JSON.stringify(d));
  const raw = (d.content||[]).map(b=>b.text||"").join("").trim();
  // Strip markdown fences if present
  let cleaned = raw.replace(/^```json\s*/i,"").replace(/^```\s*/i,"").replace(/```\s*$/i,"").trim();
  // Extract the outermost JSON object or array — handles preamble AND trailing text
  const objMatch = cleaned.match(/\{[\s\S]*\}/);
  const arrMatch = cleaned.match(/\[[\s\S]*\]/);
  const extracted = objMatch ? objMatch[0] : arrMatch ? arrMatch[0] : cleaned;
  try { return JSON.parse(extracted); }
  catch { throw new Error("AI returned invalid JSON. Raw: " + raw.slice(0,300)); }
}
async function callAIText(payload) {
  const r = await fetch("/api/chat", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify(payload) });
  const d = await r.json();
  if (!r.ok) throw new Error("API error: " + JSON.stringify(d));
  return (d.content||[]).map(b=>b.text||"").join("").trim();
}
async function structureFromText(text) {
  return callAI({ model:"gemini", max_tokens:8000, json_mode:true, system:SYSTEM, messages:[{role:"user",content:`Structure this recipe:\n\n${text}`}] });
}
async function extractRawFromImage(base64, mime) {
  return callAIText({ model:"gemini", max_tokens:8000,
    system:`You are an expert OCR and translation assistant for recipe images.
- Transcribe EVERY word faithfully and translate to clear English
- Mark unclear words as [unclear: word?]
- Preserve original measurements (vati, katori, a fistful, etc.)
- Return plain text only — no JSON, no markdown`,
    messages:[{role:"user",content:[
      {type:"image",source:{type:"base64",media_type:mime,data:base64}},
      {type:"text",text:"Transcribe and translate this handwritten recipe image into English. Flag any unclear words with [unclear: ...]."}
    ]}]
  });
}
async function estimateNutrition(recipe) {
  return callAI({ model:"gemini", max_tokens:500, json_mode:true,
    system:`You are a nutritionist. Estimate nutrition per serving. Return ONLY valid JSON, no markdown, no explanation, no extra text:
{"calories":"320 kcal","protein":"12g","carbs":"45g","fat":"8g","fibre":"4g","note":"Estimates only — actual values may vary."}`,
    messages:[{role:"user",content:`Recipe: ${recipe.name}\nServings: ${recipe.servings||"4"}\nIngredients: ${(recipe.ingredients||[]).join(", ")}\n\nReturn ONLY the JSON object, nothing else.`}]
  });
}
async function generateStory(recipe) {
  return callAIText({ model:"gemini", max_tokens:600,
    system:`You are a warm family historian who writes short, evocative narratives about family recipes. Write 2-3 sentences in a personal, heartfelt tone. Mention the contributor's name, location if available, and any cultural or family significance. Do NOT invent facts — only use what's provided.`,
    messages:[{role:"user",content:`Write a brief story for this recipe:
Name: ${recipe.name}
Contributor: ${recipe.contributor||"Unknown"}
Location: ${[recipe.contributorCity,recipe.contributorCountry].filter(Boolean).join(", ")||"Unknown"}
Description: ${recipe.description||""}
Notes: ${recipe.notes||""}
Occasion: ${(recipe.occasionTags||[]).join(", ")}
Cuisine: ${(recipe.cuisineTags||[]).join(", ")}`}]
  });
}
async function categorizeGrocery(ingredients) {
  try {
    return await callAI({ model:"gemini", max_tokens:1500, json_mode:true,
      system:`You are a grocery organiser. Given a list of recipe ingredients, group them into shopping categories. Return ONLY valid JSON:
{"Vegetables & Greens":[],"Dairy & Eggs":[],"Meat & Seafood":[],"Grains & Flours":[],"Spices & Seasonings":[],"Oils & Condiments":[],"Lentils & Pulses":[],"Fruits":[],"Nuts & Dry Fruits":[],"Other":[]}`,
      messages:[{role:"user",content:`Categorise these ingredients:\n${ingredients.join("\n")}`}]
    });
  } catch { return {"All Ingredients": ingredients}; }
}

// ─── STORAGE ──────────────────────────────────────────────────────────────────
const SUPABASE_URL = "https://zhukoxvdiehzyerrqiav.supabase.co";
const SUPABASE_KEY = "sb_publishable_C-5ShbfnXjl4z4eLtAtsiw_1OQOLgSl";
const SUPABASE_OK = !SUPABASE_URL.includes("YOUR_");

const supa = (path, opts={}) => fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
  ...opts, headers:{ "Content-Type":"application/json","apikey":SUPABASE_KEY,"Authorization":`Bearer ${SUPABASE_KEY}`,"Prefer":opts.prefer||"",...opts.headers }
});

async function loadAllRecipes() {
  if (!SUPABASE_OK) {
    return Object.keys(localStorage).filter(k=>k.startsWith("recipe:"))
      .map(k=>{try{return JSON.parse(localStorage.getItem(k));}catch{return null;}})
      .filter(Boolean).sort((a,b)=>new Date(b.createdAt)-new Date(a.createdAt));
  }
  try {
    const res = await supa("recipes?order=created_at.desc");
    if (!res.ok) return [];
    return (await res.json()).map(r=>({...r.data,id:r.id,createdAt:r.created_at}));
  } catch { return []; }
}
async function persistRecipe(recipe) {
  if (!SUPABASE_OK) { localStorage.setItem(`recipe:${recipe.id}`,JSON.stringify(recipe)); return; }
  await supa("recipes",{method:"POST",prefer:"return=minimal",body:JSON.stringify({id:recipe.id,created_at:recipe.createdAt,data:recipe})});
}
async function updateRecipe(recipe) {
  if (!SUPABASE_OK) { localStorage.setItem(`recipe:${recipe.id}`,JSON.stringify(recipe)); return; }
  await supa(`recipes?id=eq.${recipe.id}`,{method:"PATCH",prefer:"return=minimal",body:JSON.stringify({data:recipe})});
}
async function removeRecipe(id) {
  if (!SUPABASE_OK) { localStorage.removeItem(`recipe:${id}`); return; }
  try { await supa(`recipes?id=eq.${id}`,{method:"DELETE"}); } catch {}
}

// Profile storage (localStorage only — lightweight)
function loadProfiles() {
  try { return JSON.parse(localStorage.getItem("chef-profiles")||"{}"); } catch { return {}; }
}
function saveProfiles(profiles) { localStorage.setItem("chef-profiles",JSON.stringify(profiles)); }

// Meal plan storage
function loadMealPlan() {
  try { return JSON.parse(localStorage.getItem("meal-plan")||"{}"); } catch { return {}; }
}
function saveMealPlan(plan) { localStorage.setItem("meal-plan",JSON.stringify(plan)); }

// ─── UTILS ────────────────────────────────────────────────────────────────────
async function compressImage(file, maxPx=800, quality=0.65) {
  return new Promise(resolve=>{
    const reader=new FileReader();
    reader.onloadend=()=>{
      const img=new Image();
      img.onload=()=>{
        let{width,height}=img;
        if(width>maxPx||height>maxPx){
          if(width>height){height=Math.round(height*maxPx/width);width=maxPx;}
          else{width=Math.round(width*maxPx/height);height=maxPx;}
        }
        const canvas=document.createElement("canvas");
        canvas.width=width;canvas.height=height;
        canvas.getContext("2d").drawImage(img,0,0,width,height);
        resolve(canvas.toDataURL("image/jpeg",quality));
      };
      img.src=reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function scaleIngredient(ing,factor) {
  if(factor===1)return ing;
  return ing.replace(/\d+(?:\.\d+)?(?:\/\d+)?/g,match=>{
    let val;
    if(match.includes("/")){const[a,b]=match.split("/");val=(parseFloat(a)/parseFloat(b))*factor;}
    else{val=parseFloat(match)*factor;}
    if(val===Math.round(val))return String(val);
    const fracs=[[1,4],[1,3],[1,2],[2,3],[3,4],[1,8],[3,8]];
    for(const[a,b] of fracs){if(Math.abs(val-a/b)<0.07)return`${a}/${b}`;}
    return val.toFixed(1).replace(/\.0$/,"");
  });
}

function buildRecipeText(recipe, bold=false) {
  const b = s => bold ? `*${s}*` : s;
  const parts = [];
  parts.push(`🍽️ ${b(recipe.name)}`);
  if (recipe.contributor) parts.push(`by ${recipe.contributor}`);
  if (recipe.description) parts.push(`\n${recipe.description}`);

  const meta = [recipe.prepTime&&`⏱ Prep: ${recipe.prepTime}`, recipe.cookTime&&`🔥 Cook: ${recipe.cookTime}`, recipe.servings&&`👥 Serves: ${recipe.servings}`].filter(Boolean);
  if (meta.length) parts.push("\n" + meta.join("  ·  "));

  if (recipe.ingredients?.length) {
    parts.push(`\n${b("Ingredients:")}\n` + recipe.ingredients.map(i => "• " + i).join("\n"));
  }

  if (recipe.steps?.length) {
    parts.push(`\n${b("Method:")}\n` + recipe.steps.map((s, i) => `${i + 1}. ${s}`).join("\n"));
  }

  if (recipe.notes) parts.push(`\n💛 ${b("Notes:")} ${recipe.notes}`);

  parts.push("\n— Shared from Rasoi Ghar 📜");
  return parts.join("\n");
}

function shareRecipe(recipe) {
  const text = buildRecipeText(recipe, false);
  if (navigator.share) { navigator.share({ title: recipe.name, text }).catch(() => {}); }
  else { navigator.clipboard.writeText(text).then(() => alert("Recipe copied to clipboard!")); }
}

function whatsappShare(recipe) {
  const text = encodeURIComponent(buildRecipeText(recipe, true));
  window.open(`https://wa.me/?text=${text}`, "_blank");
}

// ─── IDENTITY MODALS ─────────────────────────────────────────────────────────
function IdentityModal({ onDone }) {
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [mode, setMode] = useState("choose"); // choose | owner-pin
  const [error, setError] = useState("");

  const signInAsGuest = () => {
    const n = name.trim();
    if (!n || n.toLowerCase() === "anonymous") { setError("Please enter your real name."); return; }
    saveIdentity({ name: n, isOwner: false });
    onDone({ name: n, isOwner: false });
  };

  const signInAsOwner = () => {
    if (pin === OWNER_PIN) {
      const identity = { name: name.trim() || "Owner", isOwner: true };
      saveIdentity(identity);
      onDone(identity);
    } else {
      setError("Incorrect PIN. Try again.");
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: C.overlay, zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: C.bg, borderRadius: 18, width: "100%", maxWidth: 420, boxShadow: "0 24px 70px rgba(0,0,0,.4)", animation: "fadeIn .3s ease", overflow: "hidden" }}>
        {/* Header */}
        <div style={{ background: C.headerBg, padding: "28px 28px 22px", textAlign: "center", position:"relative", overflow:"hidden" }}>
          <div style={{position:"absolute",inset:0,opacity:.06,backgroundImage:C.headerPattern,backgroundSize:"22px 22px",pointerEvents:"none"}}/>
          <div style={{ fontSize: 44, marginBottom: 10, position:"relative" }}>📜</div>
          <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: 24, color: "#F5E6C8", margin: "0 0 10px", position:"relative" }}>Welcome to Rasoi Ghar</h2>
          <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"center",position:"relative"}}>
            <div style={{height:1,width:24,background:`linear-gradient(to right,transparent,${C.amber})`}}/>
            <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",fontWeight:600,fontSize:13,color:"#E8D5A0",margin:0,letterSpacing:1}}>Preserving grandma's secrets for generations</p>
            <div style={{height:1,width:24,background:`linear-gradient(to left,transparent,${C.amber})`}}/>
          </div>
        </div>

        <div style={{ padding: "24px 28px 28px" }}>
          {mode === "choose" && (
            <>
              <label style={LB({ marginBottom: 6 })}>Your name <span style={{ color: C.red }}>*</span></label>
              <input value={name} onChange={e => { setName(e.target.value); setError(""); }}
                placeholder="e.g. Priya, Rajan, Ammamma…"
                style={{ ...IS, marginBottom: 16 }} />

              {error && <p style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 13, color: C.red, margin: "-10px 0 14px" }}>{error}</p>}

              <button onClick={signInAsGuest}
                style={{ ...BP, marginBottom: 12 }}>
                👤 Continue as Family Member
              </button>

              <button onClick={() => { setMode("owner-pin"); setError(""); }}
                style={{ width: "100%", padding: "11px", background: "none", border: `1.5px solid ${C.border}`, borderRadius: 9, fontFamily: "Cormorant Garamond, serif", fontSize: 15, color: C.muted, cursor: "pointer" }}>
                🔑 Sign in as Owner
              </button>

              <p style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 12, color: C.border, textAlign: "center", marginTop: 16, lineHeight: 1.6 }}>
                Family members can browse, comment, and add recipes.<br />
                The owner can also edit and remove any recipe.
              </p>
            </>
          )}

          {mode === "owner-pin" && (
            <>
              <button onClick={() => { setMode("choose"); setError(""); setPin(""); }}
                style={{ background: "none", border: "none", fontFamily: "Cormorant Garamond, serif", fontSize: 14, color: C.muted, cursor: "pointer", marginBottom: 16, padding: 0 }}>
                ← Back
              </button>

              <label style={LB({ marginBottom: 6 })}>Your name</label>
              <input value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g. Your name…"
                style={{ ...IS, marginBottom: 12 }} />

              <label style={LB({ marginBottom: 6 })}>Owner PIN <span style={{ color: C.red }}>*</span></label>
              <input type="password" value={pin} onChange={e => { setPin(e.target.value); setError(""); }}
                placeholder="Enter owner PIN…"
                style={{ ...IS, marginBottom: 16 }}
                onKeyDown={e => e.key === "Enter" && signInAsOwner()} />

              {error && <p style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 13, color: C.red, margin: "-10px 0 14px" }}>{error}</p>}

              <button onClick={signInAsOwner} style={BP}>🔑 Sign in as Owner</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── SMALL COMPONENTS ────────────────────────────────────────────────────────
function Spinner({label="AI is working its magic…"}){
  return(
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14,padding:"28px 0"}}>
      <div style={{width:44,height:44,border:`3px solid ${C.border}`,borderTopColor:C.amber,borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>
      <p style={{fontFamily:"Cormorant Garamond, serif",color:C.muted,fontSize:16,fontStyle:"italic",margin:0}}>{label}</p>
    </div>
  );
}

function PhotoUploader({photos=[],onChange}){
  const fileRef=useRef();
  const addPhotos=async files=>{
    const newPhotos=[];
    for(const f of Array.from(files).slice(0,5-photos.length)){
      const compressed=await compressImage(f,1000,0.7);
      newPhotos.push(compressed);
    }
    onChange([...photos,...newPhotos]);
  };
  return(
    <div>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:8}}>
        {photos.map((p,i)=>(
          <div key={i} style={{position:"relative",width:80,height:80}}>
            <img src={p} alt="" style={{width:80,height:80,objectFit:"cover",borderRadius:8,border:`1px solid ${C.border}`}}/>
            <button onClick={()=>onChange(photos.filter((_,j)=>j!==i))} style={{position:"absolute",top:-6,right:-6,width:20,height:20,borderRadius:"50%",background:C.red,border:"none",color:"#fff",fontSize:11,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
          </div>
        ))}
        {photos.length<5&&(
          <div onClick={()=>fileRef.current.click()} style={{width:80,height:80,border:`2px dashed ${C.border}`,borderRadius:8,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",cursor:"pointer",color:C.muted,fontSize:11,fontFamily:"Cormorant Garamond, serif",gap:4}}>
            <span style={{fontSize:22}}>📸</span>Add
            <input ref={fileRef} type="file" accept="image/*" multiple style={{display:"none"}} onChange={e=>addPhotos(e.target.files)}/>
          </div>
        )}
      </div>
      <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,margin:0}}>Up to 5 photos — optional</p>
    </div>
  );
}

function VideoUploader({videoFile,onChange}){
  const fileRef=useRef();
  const [warn,setWarn]=useState("");
  const handleFile=f=>{
    if(!f)return;
    if(f.size>50*1024*1024)setWarn("Video over 50MB — consider a YouTube link for large files.");
    else setWarn("");
    const reader=new FileReader();
    reader.onloadend=()=>onChange(reader.result);
    reader.readAsDataURL(f);
  };
  if(videoFile)return(
    <div style={{background:C.parchment,border:`1px solid ${C.border}`,borderRadius:9,padding:"10px 14px",display:"flex",alignItems:"center",gap:10}}>
      <span>🎥</span>
      <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.green,flex:1}}>Video uploaded and ready</span>
      <button onClick={()=>onChange(null)} style={{background:"none",border:"none",cursor:"pointer",color:C.red,fontFamily:"Cormorant Garamond, serif",fontSize:13}}>✕ Remove</button>
    </div>
  );
  return(
    <div>
      <div onClick={()=>fileRef.current.click()} style={{border:`2px dashed ${C.border}`,borderRadius:9,padding:"12px",textAlign:"center",cursor:"pointer",background:C.parchment}}>
        <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,margin:0}}>📹 Or upload MP4, MOV, etc.</p>
        <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.border,margin:"4px 0 0"}}>Recommended: under 50MB</p>
        <input ref={fileRef} type="file" accept="video/*" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
      </div>
      {warn&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.amber,margin:"6px 0 0"}}>⚠️ {warn}</p>}
    </div>
  );
}

function TagPicker({items,selected=[],onChange,colorActive=C.amber,colorInactive=C.border}){
  const toggle=id=>{
    const s=[...(selected||[])];
    const idx=s.indexOf(id);
    if(idx>=0)s.splice(idx,1); else s.push(id);
    onChange(s);
  };
  return(
    <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
      {items.map(o=>{
        const active=(selected||[]).includes(o.id);
        return(
          <button key={o.id} onClick={()=>toggle(o.id)}
            style={{padding:"5px 12px",borderRadius:20,border:`1.5px solid ${active?colorActive:colorInactive}`,background:active?`${colorActive}20`:"transparent",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:active?colorActive:C.muted,fontWeight:active?600:400,transition:"all .15s"}}>
            {o.emoji} {o.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── NUTRITION SECTION ────────────────────────────────────────────────────────
function NutritionSection({recipe,onUpdate}){
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");
  const n=recipe.nutrition;

  const estimate=async()=>{
    setLoading(true);setError("");
    try{
      const data=await estimateNutrition(recipe);
      if(!data||typeof data!=="object")throw new Error("Invalid response");
      await onUpdate({...recipe,nutrition:data});
    }catch(e){setError("Could not estimate nutrition: " + (e.message||"Unknown error"));console.error("Nutrition error:",e);}
    setLoading(false);
  };

  if(loading)return <div style={{marginBottom:20}}><Spinner label="Estimating nutrition…"/></div>;

  return(
    <div style={{marginBottom:26}}>
      <h2 style={SH}>🥗 Nutrition (per serving)</h2>
      {n?(
        <>
          <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:8}}>
            {[["🔥","Calories",n.calories],["💪","Protein",n.protein],["🍞","Carbs",n.carbs],["🧈","Fat",n.fat],["🌿","Fibre",n.fibre]].map(([emoji,label,val])=>val?(
              <div key={label} style={{...MB,minWidth:80}}>
                <div style={ML}>{emoji} {label}</div>
                <div style={{...MV,fontSize:14}}>{val}</div>
              </div>
            ):null)}
          </div>
          {n.note&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,fontStyle:"italic",margin:0}}>{n.note}</p>}
          <button onClick={estimate} style={{marginTop:8,background:"none",border:`1px solid ${C.border}`,borderRadius:7,padding:"5px 12px",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,cursor:"pointer"}}>🔄 Re-estimate</button>
        </>
      ):(
        <div>
          <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,marginBottom:10}}>AI can estimate the approximate nutritional values for this recipe.</p>
          <button onClick={estimate} style={{...BP,width:"auto",padding:"9px 22px",fontSize:14}}>✨ Estimate Nutrition</button>
        </div>
      )}
      {error&&<p style={{color:C.red,fontFamily:"Cormorant Garamond, serif",fontSize:13,margin:"8px 0 0"}}>{error}</p>}
    </div>
  );
}

// ─── COMMENTS SECTION ─────────────────────────────────────────────────────────
function CommentsSection({recipe,onUpdate}){
  const [name,setName]=useState("");
  const [text,setText]=useState("");
  const [saving,setSaving]=useState(false);
  const comments=recipe.comments||[];

  const addComment=async()=>{
    if(!name.trim()||!text.trim())return;
    setSaving(true);
    const c={id:Date.now().toString(),author:name.trim(),text:text.trim(),date:new Date().toISOString()};
    await onUpdate({...recipe,comments:[...comments,c]});
    setName("");setText("");setSaving(false);
  };

  return(
    <div style={{marginBottom:26}}>
      <h2 style={SH}>💬 Family Comments & Memories ({comments.length})</h2>
      {comments.length===0&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,marginBottom:16,fontStyle:"italic"}}>Be the first to add a memory or tip about this recipe.</p>}
      {comments.map(c=>(
        <div key={c.id} style={{background:C.parchment,border:`1px solid ${C.border}`,borderRadius:9,padding:"12px 16px",marginBottom:10}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
            <span style={{fontFamily:"Playfair Display, serif",fontSize:14,color:C.brown,fontWeight:600}}>👤 {c.author}</span>
            <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted}}>{new Date(c.date).toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}</span>
          </div>
          <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.text,margin:0,lineHeight:1.65,fontStyle:"italic"}}>{c.text}</p>
        </div>
      ))}
      <div style={{background:`${C.amber}10`,border:`1px solid ${C.amber}30`,borderRadius:9,padding:"14px 16px",marginTop:12}}>
        <p style={{...LB(),marginBottom:8}}>Add a comment</p>
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="Your name" style={{...IS,marginBottom:8}}/>
        <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Share a memory, tip, or variation…" style={{...IS,minHeight:70,resize:"vertical",marginBottom:8}}/>
        <button onClick={addComment} disabled={saving||!name.trim()||!text.trim()}
          style={{...BP,opacity:saving||!name.trim()||!text.trim()?0.6:1}}>
          {saving?"Saving…":"💬 Add Comment"}
        </button>
      </div>
    </div>
  );
}

// ─── ADD VIDEO SECTION ────────────────────────────────────────────────────────
function AddVideoSection({recipe,onUpdate}){
  const [open,setOpen]=useState(false);
  const [videoUrl,setVideoUrl]=useState(recipe.videoUrl||"");
  const [videoFile,setVideoFile]=useState(null);
  const [saving,setSaving]=useState(false);
  const [saved,setSaved]=useState(false);
  const fileRef=useRef();
  const hasExisting=recipe.videoUrl||recipe.videoFile;

  const handleFile=f=>{
    if(!f)return;
    const reader=new FileReader();
    reader.onloadend=()=>setVideoFile(reader.result);
    reader.readAsDataURL(f);
  };
  const save=async()=>{
    setSaving(true);
    await onUpdate({...recipe,videoUrl:videoUrl.trim(),videoFile:videoFile||recipe.videoFile||null});
    setSaving(false);setSaved(true);
    setTimeout(()=>{setSaved(false);setOpen(false);},1500);
  };

  if(!open)return(
    <button onClick={()=>setOpen(true)} style={{width:"100%",padding:"10px",background:"none",border:`1.5px solid ${C.border}`,borderRadius:8,fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,cursor:"pointer",marginBottom:10}}>
      {hasExisting?"🎥 Update Video":"🎥 Add a Video to this Recipe"}
    </button>
  );
  return(
    <div style={{background:`${C.amber}10`,border:`1px solid ${C.amber}40`,borderRadius:10,padding:"16px 18px",marginBottom:16}}>
      <p style={{fontFamily:"Playfair Display, serif",fontSize:15,color:C.brown,margin:"0 0 12px"}}>🎥 {hasExisting?"Update Video":"Add a Video"}</p>
      <label style={LB({marginBottom:5})}>YouTube or video link</label>
      <input value={videoUrl} onChange={e=>{setVideoUrl(e.target.value);if(e.target.value)setVideoFile(null);}}
        placeholder="https://youtube.com/watch?v=…" style={{...IS,marginBottom:10}} disabled={!!videoFile}/>
      {!videoUrl&&(
        <>
          <label style={LB({marginBottom:5})}>Or upload a video file</label>
          {videoFile
            ?<div style={{background:C.parchment,border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
               <span>🎥</span><span style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.green,flex:1}}>Video ready</span>
               <button onClick={()=>setVideoFile(null)} style={{background:"none",border:"none",cursor:"pointer",color:C.red,fontSize:13,fontFamily:"Cormorant Garamond, serif"}}>✕ Remove</button>
             </div>
            :<div onClick={()=>fileRef.current.click()} style={{border:`2px dashed ${C.border}`,borderRadius:8,padding:"12px",textAlign:"center",cursor:"pointer",background:C.parchment,marginBottom:10}}>
               <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,margin:0}}>📹 Click to upload MP4, MOV, etc.</p>
               <input ref={fileRef} type="file" accept="video/*" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
             </div>
          }
        </>
      )}
      <div style={{display:"flex",gap:10}}>
        <button onClick={()=>{setOpen(false);setVideoUrl(recipe.videoUrl||"");setVideoFile(null);}} style={{flex:1,padding:"9px",background:"none",border:`1.5px solid ${C.border}`,borderRadius:7,fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,cursor:"pointer"}}>Cancel</button>
        <button onClick={save} disabled={saving||saved} style={{flex:2,padding:"9px",background:saved?C.green:`linear-gradient(135deg,${C.amber},${C.sienna})`,border:"none",borderRadius:7,fontFamily:"Cormorant Garamond, serif",fontSize:14,color:"#fff",cursor:"pointer"}}>
          {saved?"✅ Saved!":saving?"Saving…":"💾 Save Video"}
        </button>
      </div>
    </div>
  );
}

// ─── RECIPE CARD ─────────────────────────────────────────────────────────────
function RecipeCard({recipe,onClick,onToggleFavourite}){
  const [hover,setHover]=useState(false);
  const icon={voice:"🎙️",scan:"📷",manual:"✍️"}[recipe.inputType]||"📖";
  const badge={voice:"Voice",scan:"Scanned",manual:"Typed"}[recipe.inputType]||"";
  const bColor={voice:C.amber,scan:C.sienna,manual:C.green}[recipe.inputType]||C.amber;
  const di=DIETARY.find(d=>d.id===recipe.dietaryType);
  const cover=recipe.photos?.[0]||recipe.imageBase64;

  return(
    <div onClick={onClick} onMouseEnter={()=>setHover(true)} onMouseLeave={()=>setHover(false)}
      style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:13,overflow:"hidden",cursor:"pointer",position:"relative",
        transition:"transform .18s,box-shadow .18s",transform:hover?"translateY(-4px)":"none",
        boxShadow:hover?"0 10px 30px rgba(74,32,16,.18)":"0 2px 8px rgba(74,32,16,.07)",animation:"fadeIn .4s ease both"}}>
      {cover&&<img src={cover} alt={recipe.name} style={{width:"100%",height:130,objectFit:"cover"}}/>}
      {/* Public/Private badge */}
      {recipe.isPublic&&<span style={{position:"absolute",top:10,left:10,background:"rgba(45,96,128,0.85)",color:"#fff",borderRadius:12,padding:"2px 8px",fontSize:11,fontFamily:"Cormorant Garamond, serif"}}>🌐 Public</span>}
      {/* Adapted badge */}
      {recipe.adaptedFrom&&<span style={{position:"absolute",top:cover?140:10,left:10,background:`${C.amber}dd`,color:"#fff",borderRadius:12,padding:"2px 8px",fontSize:11,fontFamily:"Cormorant Garamond, serif"}}>🔀 Adapted</span>}
      <button onClick={e=>{e.stopPropagation();onToggleFavourite(recipe);}}
        style={{position:"absolute",top:10,right:10,background:"rgba(255,255,255,0.9)",border:"none",borderRadius:"50%",width:32,height:32,cursor:"pointer",fontSize:16,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 6px rgba(0,0,0,.15)"}}>
        {recipe.isFavourite?"❤️":"🤍"}
      </button>
      <div style={{padding:"14px 18px 16px"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8,flexWrap:"wrap"}}>
          <span style={{fontSize:11,fontFamily:"Cormorant Garamond, serif",fontWeight:600,color:bColor,background:`${bColor}18`,padding:"2px 9px",borderRadius:20}}>{icon} {badge}</span>
          {di&&<span style={{fontSize:11,fontFamily:"Cormorant Garamond, serif",fontWeight:600,color:di.color,background:`${di.color}18`,padding:"2px 9px",borderRadius:20,border:`1px solid ${di.color}40`}}>{di.emoji} {di.label}</span>}
          {recipe.cuisineTags?.slice(0,1).map(ct=>{const cu=CUISINES.find(c=>c.id===ct);return cu?<span key={ct} style={{fontSize:11,fontFamily:"Cormorant Garamond, serif",color:C.blue,background:`${C.blue}15`,padding:"2px 9px",borderRadius:20}}>{cu.emoji} {cu.label}</span>:null;})}
          {recipe.audioBase64&&<span title="Voice note">🔊</span>}
          {(recipe.videoUrl||recipe.videoFile)&&<span title="Has video">🎥</span>}
          {recipe.comments?.length>0&&<span style={{fontSize:11,color:C.muted,fontFamily:"Cormorant Garamond, serif"}}>💬{recipe.comments.length}</span>}
        </div>
        <h3 style={{fontFamily:"Playfair Display, serif",fontSize:19,color:C.brown,margin:"0 0 6px",lineHeight:1.3}}>{recipe.name}</h3>
        {recipe.description&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14.5,color:C.muted,margin:"0 0 10px",lineHeight:1.55,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{recipe.description}</p>}
        <div style={{display:"flex",gap:14,marginBottom:8,flexWrap:"wrap"}}>
          {recipe.cookTime&&<span style={{fontSize:13,fontFamily:"Cormorant Garamond, serif",color:C.muted}}>🕐 {recipe.cookTime}</span>}
          {recipe.servings&&<span style={{fontSize:13,fontFamily:"Cormorant Garamond, serif",color:C.muted}}>👥 {recipe.servings}</span>}
          {recipe.ingredients?.length>0&&<span style={{fontSize:13,fontFamily:"Cormorant Garamond, serif",color:C.muted}}>🧂 {recipe.ingredients.length} ing.</span>}
          {recipe.nutrition?.calories&&<span style={{fontSize:13,fontFamily:"Cormorant Garamond, serif",color:C.muted}}>🔥 {recipe.nutrition.calories}</span>}
        </div>
        {recipe.occasionTags?.slice(0,2).map(ot=>{const o=OCCASIONS.find(x=>x.id===ot);return o?<span key={ot} style={{fontSize:11,fontFamily:"Cormorant Garamond, serif",background:`${C.amber}18`,color:C.amber,padding:"2px 8px",borderRadius:20,marginRight:4}}>{o.emoji} {o.label}</span>:null;})}
        {recipe.contributor&&(
          <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",fontSize:13,color:C.sienna,margin:"6px 0 0"}}>
            by {recipe.contributor}
            {(recipe.contributorCity||recipe.contributorCountry)&&(
              <span style={{color:C.muted,fontStyle:"normal"}}> · 📍 {[recipe.contributorCity,recipe.contributorCountry].filter(Boolean).join(", ")}</span>
            )}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── INPUT TABS ───────────────────────────────────────────────────────────────
function VoiceTab({onStructured}){
  const [lang,setLang]=useState("hi-IN");
  const [stage,setStage]=useState("idle");
  const [transcript,setTranscript]=useState("");
  const [interim,setInterim]=useState("");
  const [audioUrl,setAudioUrl]=useState(null);
  const [audioBase64,setAudioBase64]=useState(null);
  const [error,setError]=useState(null);
  const recRef=useRef(null);
  const mrRef=useRef(null);
  const chunksRef=useRef([]);
  const finalRef=useRef("");

  const start=async()=>{
    setError(null);setTranscript("");setInterim("");setAudioUrl(null);setAudioBase64(null);
    chunksRef.current=[];finalRef.current="";
    try{
      const stream=await navigator.mediaDevices.getUserMedia({audio:true});
      const mr=new MediaRecorder(stream);
      mrRef.current=mr;
      mr.ondataavailable=e=>e.data.size>0&&chunksRef.current.push(e.data);
      mr.onstop=()=>{
        const blob=new Blob(chunksRef.current,{type:"audio/webm"});
        setAudioUrl(URL.createObjectURL(blob));
        const reader=new FileReader();
        reader.onloadend=()=>setAudioBase64(reader.result);
        reader.readAsDataURL(blob);
        stream.getTracks().forEach(t=>t.stop());
      };
      mr.start(250);
      const SR=window.SpeechRecognition||window.webkitSpeechRecognition;
      if(SR){
        const rec=new SR();
        rec.lang=lang;rec.continuous=true;rec.interimResults=true;
        rec.onresult=e=>{let fin="",int="";for(let i=0;i<e.results.length;i++){if(e.results[i].isFinal)fin+=e.results[i][0].transcript+" ";else int+=e.results[i][0].transcript;}finalRef.current=fin;setTranscript(fin);setInterim(int);};
        rec.onerror=()=>{};rec.onend=()=>setInterim("");rec.start();
        recRef.current=rec;
      }
      setStage("recording");
    }catch{setError("Microphone access denied. Please allow microphone and try again.");}
  };
  const stop=()=>{recRef.current?.stop();mrRef.current?.stop();setTranscript(finalRef.current);setInterim("");setStage("idle");};
  const process=async()=>{
    const text=transcript.trim();
    if(!text){setError("No transcript. Please try again.");return;}
    setStage("processing");setError(null);
    try{
      const s=await structureFromText(text);
      onStructured({...s,audioBase64,rawTranscription:text,inputType:"voice",originalLanguage:LANGUAGES.find(l=>l.code===lang)?.label||lang});
    }catch{setError("Failed to structure. Please try again.");setStage("idle");}
  };
  if(stage==="processing")return <Spinner label="Translating & structuring…"/>;
  return(
    <div style={{animation:"fadeIn .3s ease"}}>
      <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.muted,marginBottom:16,lineHeight:1.6}}>Choose a language, press record, and let the chef dictate naturally.</p>
      <div style={{marginBottom:18}}>
        <label style={LB()}>Language being spoken</label>
        <select value={lang} onChange={e=>setLang(e.target.value)} disabled={stage==="recording"} style={{...IS,cursor:"pointer"}}>
          {LANGUAGES.map(l=><option key={l.code} value={l.code}>{l.label}</option>)}
        </select>
      </div>
      <div style={{textAlign:"center",margin:"22px 0 18px"}}>
        {stage!=="recording"
          ?<button onClick={start} style={{width:88,height:88,borderRadius:"50%",background:`linear-gradient(145deg,${C.amber},${C.sienna})`,border:"none",cursor:"pointer",fontSize:32,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto",boxShadow:"0 5px 20px rgba(180,112,16,.4)"}}>🎙️</button>
          :<button onClick={stop} style={{width:88,height:88,borderRadius:"50%",background:C.red,border:"4px solid rgba(255,255,255,.3)",cursor:"pointer",fontSize:30,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto",animation:"pulse 1.6s ease-in-out infinite"}}>⏹</button>}
        <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,marginTop:10}}>{stage==="recording"?"🔴 Recording… tap to stop":"Tap to start recording"}</p>
      </div>
      {(transcript||interim)&&<div style={{background:C.parchment,border:`1px solid ${C.border}`,borderRadius:9,padding:14,marginBottom:14,maxHeight:130,overflowY:"auto"}}><p style={{fontFamily:"Cormorant Garamond, serif",fontSize:11,color:C.muted,textTransform:"uppercase",letterSpacing:1,margin:"0 0 6px"}}>Live Transcript</p><p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.text,margin:0,lineHeight:1.65}}>{transcript}<span style={{color:C.border}}>{interim}</span></p></div>}
      {audioUrl&&<div style={{marginBottom:16}}><p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.green,margin:"0 0 7px"}}>🔒 Voice note captured</p><audio src={audioUrl} controls style={{width:"100%",borderRadius:8}}/></div>}
      {error&&<p style={{color:C.red,fontFamily:"Cormorant Garamond, serif",fontSize:14,marginBottom:10}}>{error}</p>}
      {transcript&&stage!=="recording"&&<button onClick={process} style={BP}>✨ Translate & Structure Recipe</button>}
    </div>
  );
}

function ScanTab({onStructured}){
  const [preview,setPreview]=useState(null);
  const [b64,setB64]=useState(null);
  const [mime,setMime]=useState("image/jpeg");
  const [stage,setStage]=useState("idle");
  const [rawText,setRawText]=useState("");
  const [detectedLang,setDetectedLang]=useState("");
  const [error,setError]=useState(null);
  const [pasteHint,setPasteHint]=useState(false);
  const fileRef=useRef();
  const dropRef=useRef();

  // ── handle any image source ──────────────────────────────────────────────
  const handleFile=async f=>{
    if(!f)return;
    const c=await compressImage(f);
    setMime("image/jpeg");setPreview(c);setB64(c.split(",")[1]);setRawText("");setStage("idle");setError(null);
  };

  // ── clipboard paste — button ──────────────────────────────────────────────
  const handlePasteButton=async()=>{
    setError(null);
    try{
      const items=await navigator.clipboard.read();
      let found=false;
      for(const item of items){
        const imgType=item.types.find(t=>t.startsWith("image/"));
        if(imgType){
          const blob=await item.getType(imgType);
          const file=new File([blob],"pasted."+imgType.split("/")[1],{type:imgType});
          await handleFile(file);
          found=true;break;
        }
      }
      if(!found)setError("No image found in clipboard. Copy an image first, then try again.");
    }catch(e){
      // Clipboard API blocked — fall back to showing keyboard hint
      setPasteHint(true);
      setError("Click inside the upload area below, then press Ctrl+V (or Cmd+V on Mac).");
    }
  };

  // ── keyboard paste — Ctrl+V / Cmd+V anywhere in this tab ─────────────────
  useEffect(()=>{
    const onPaste=async e=>{
      if(stage==="review"||stage==="extracting"||stage==="structuring")return;
      const items=[...e.clipboardData.items];
      const imgItem=items.find(i=>i.type.startsWith("image/"));
      if(!imgItem)return;
      e.preventDefault();
      const file=imgItem.getAsFile();
      if(file)await handleFile(file);
    };
    window.addEventListener("paste",onPaste);
    return()=>window.removeEventListener("paste",onPaste);
  },[stage]);

  // ── extract & structure ───────────────────────────────────────────────────
  const extract=async()=>{
    if(!b64)return;
    setStage("extracting");setError(null);
    try{
      const text=await extractRawFromImage(b64,mime);
      const langHints=[["Gujarati",/gujarati|gu-in|ગ/i],["Hindi",/hindi|hi-in|ह/i],["Tamil",/tamil|ta-in|த/i],["Telugu",/telugu|te-in|త/i],["Marathi",/marathi|mr-in/i],["Bengali",/bengali|bn-in/i]];
      const found=langHints.find(([,rx])=>rx.test(text));
      setDetectedLang(found?found[0]:"");setRawText(text);setStage("review");
    }catch(e){setError("Error: "+e.message);setStage("idle");}
  };
  const structure=async()=>{
    if(!rawText.trim())return;
    setStage("structuring");setError(null);
    try{
      const s=await structureFromText(rawText);
      onStructured({...s,imageBase64:preview,inputType:"scan",originalLanguage:detectedLang||"Unknown",rawTranscription:rawText});
    }catch(e){setError("Error: "+e.message);setStage("review");}
  };

  if(stage==="extracting")return <Spinner label="Reading handwriting & translating…"/>;
  if(stage==="structuring")return <Spinner label="Structuring your recipe…"/>;
  if(stage==="review")return(
    <div style={{animation:"fadeIn .3s ease"}}>
      <div style={{background:`${C.green}15`,border:`1px solid ${C.green}40`,borderRadius:8,padding:"10px 14px",marginBottom:14}}>
        <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.green,margin:"0 0 2px",fontWeight:600}}>✅ Extracted{detectedLang?` from ${detectedLang}`:""} — review & correct before saving</p>
        <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,margin:0}}>Fix any mistakes in the translation below, then tap Structure Recipe.</p>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:14}}>
        <div>
          <p style={{...LB(),marginBottom:6}}>Original scan</p>
          <img src={preview} alt="scan" style={{width:"100%",borderRadius:8,border:`1px solid ${C.border}`}}/>
        </div>
        <div>
          <p style={{...LB(),marginBottom:6}}>Translated text <span style={{color:C.amber,fontStyle:"italic",textTransform:"none",letterSpacing:0}}>— edit if needed</span></p>
          <textarea value={rawText} onChange={e=>setRawText(e.target.value)} style={{...IS,minHeight:200,resize:"vertical",lineHeight:1.7,fontSize:14}}/>
        </div>
      </div>
      {rawText.includes("[unclear:")&&<div style={{background:`${C.amber}15`,border:`1px solid ${C.amber}40`,borderRadius:8,padding:"9px 13px",marginBottom:12}}><p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.amber,margin:0}}>⚠️ Some words are marked <strong>[unclear: ...]</strong> — please review and correct them.</p></div>}
      {error&&<p style={{color:C.red,fontFamily:"Cormorant Garamond, serif",fontSize:14,marginBottom:10}}>{error}</p>}
      <div style={{display:"flex",gap:10}}>
        <button onClick={()=>{setStage("idle");setRawText("");setPreview(null);setB64(null);}} style={{flex:1,padding:"11px 0",background:"none",border:`1.5px solid ${C.border}`,borderRadius:8,fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.muted,cursor:"pointer"}}>← Rescan</button>
        <button onClick={structure} style={{...BP,flex:2,padding:"11px 0"}}>✨ Structure Recipe</button>
      </div>
    </div>
  );

  return(
    <div style={{animation:"fadeIn .3s ease"}}>
      <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.muted,marginBottom:14,lineHeight:1.6}}>
        Photograph or screenshot a handwritten recipe in any language. You'll review the translation before saving.
      </p>

      {/* ── Three input options ── */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>

        {/* Paste from clipboard */}
        <button onClick={handlePasteButton}
          style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6,padding:"18px 10px",background:`${C.amber}10`,border:`2px dashed ${C.amber}60`,borderRadius:12,cursor:"pointer",transition:"background .15s"}}
          onMouseEnter={e=>e.currentTarget.style.background=`${C.amber}20`}
          onMouseLeave={e=>e.currentTarget.style.background=`${C.amber}10`}>
          <span style={{fontSize:28}}>📋</span>
          <span style={{fontFamily:"Playfair Display, serif",fontSize:14,color:C.brown,fontWeight:600}}>Paste Image</span>
          <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,textAlign:"center"}}>Ctrl+V / Cmd+V</span>
        </button>

        {/* Upload file */}
        <button onClick={()=>fileRef.current.click()}
          style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:6,padding:"18px 10px",background:C.parchment,border:`2px dashed ${C.border}`,borderRadius:12,cursor:"pointer",transition:"background .15s"}}
          onMouseEnter={e=>e.currentTarget.style.background=C.tag}
          onMouseLeave={e=>e.currentTarget.style.background=C.parchment}>
          <span style={{fontSize:28}}>📁</span>
          <span style={{fontFamily:"Playfair Display, serif",fontSize:14,color:C.brown,fontWeight:600}}>Upload File</span>
          <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,textAlign:"center"}}>Photo, screenshot or PDF</span>
          <input ref={fileRef} type="file" accept="image/*,application/pdf" style={{display:"none"}} onChange={e=>handleFile(e.target.files[0])}/>
        </button>
      </div>

      {/* Keyboard paste hint */}
      <div style={{background:`${C.blue}08`,border:`1px solid ${C.blue}25`,borderRadius:8,padding:"9px 14px",marginBottom:14,display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:14}}>💡</span>
        <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.blue,margin:0,lineHeight:1.5}}>
          You can also just press <strong>Ctrl+V</strong> (Windows) or <strong>Cmd+V</strong> (Mac) anywhere on this screen to instantly paste a copied screenshot.
        </p>
      </div>

      {/* Preview */}
      {preview&&(
        <div style={{position:"relative",marginBottom:12}}>
          <img src={preview} alt="scan" style={{width:"100%",maxHeight:260,objectFit:"contain",borderRadius:10,border:`1px solid ${C.border}`,background:C.parchment}}/>
          <button onClick={()=>{setPreview(null);setB64(null);setError(null);}}
            style={{position:"absolute",top:8,right:8,background:"rgba(0,0,0,0.55)",border:"none",borderRadius:"50%",width:28,height:28,cursor:"pointer",color:"#fff",fontSize:14,display:"flex",alignItems:"center",justifyContent:"center"}}>✕</button>
          <div style={{position:"absolute",bottom:8,left:8,background:"rgba(0,0,0,0.55)",borderRadius:6,padding:"3px 10px"}}>
            <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:"#fff"}}>✓ Image ready</span>
          </div>
        </div>
      )}

      {error&&<p style={{color:C.red,fontFamily:"Cormorant Garamond, serif",fontSize:14,marginBottom:10}}>{error}</p>}
      {b64&&<button onClick={extract} style={BP}>🔍 Extract & Translate Text</button>}
    </div>
  );
}

function TypeTab({onStructured}){
  const [text,setText]=useState("");
  const [stage,setStage]=useState("idle");
  const [error,setError]=useState(null);
  const process=async()=>{
    if(!text.trim())return;
    setStage("processing");setError(null);
    try{const s=await structureFromText(text);onStructured({...s,rawTranscription:text,inputType:"manual",originalLanguage:"English"});}
    catch{setError("Failed. Please try again.");setStage("idle");}
  };
  if(stage==="processing")return <Spinner label="Structuring your recipe…"/>;
  return(
    <div style={{animation:"fadeIn .3s ease"}}>
      <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.muted,marginBottom:12,lineHeight:1.6}}>Type or paste the recipe in any form — rough notes, prose, or a list.</p>
      <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="e.g. Biryani — soak basmati 30 mins. Fry onions in ghee till golden…" style={{...IS,minHeight:170,resize:"vertical",lineHeight:1.65}}/>
      {error&&<p style={{color:C.red,fontFamily:"Cormorant Garamond, serif",fontSize:14,marginTop:6}}>{error}</p>}
      {text.trim()&&<button onClick={process} style={{...BP,marginTop:12}}>✨ Structure This Recipe</button>}
    </div>
  );
}

// ─── RECIPE PREVIEW ───────────────────────────────────────────────────────────
function RecipePreview({recipe,onConfirm,onBack}){
  const [r,setR]=useState({photos:[],occasionTags:[],cuisineTags:[],videoUrl:"",isPublic:false,...recipe});
  const [saving,setSaving]=useState(false);
  const up=(f,v)=>setR(p=>({...p,[f]:v}));
  return(
    <div style={{animation:"fadeIn .3s ease"}}>
      <div style={{background:`${C.green}15`,border:`1px solid ${C.green}40`,borderRadius:8,padding:"10px 14px",marginBottom:20}}>
        <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.green,margin:0}}>✅ Recipe structured! Review and edit before saving to the vault.</p>
      </div>
      {/* Dietary */}
      <div style={{marginBottom:14}}>
        <label style={LB({marginBottom:8})}>Dietary Type</label>
        <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
          {DIETARY.filter(d=>d.id!=="all").map(d=>{const active=r.dietaryType===d.id;return(
            <button key={d.id} onClick={()=>up("dietaryType",d.id)} style={{padding:"6px 13px",borderRadius:20,border:`1.5px solid ${active?d.color:C.border}`,background:active?`${d.color}20`:"transparent",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:active?d.color:C.muted,fontWeight:active?600:400}}>{d.emoji} {d.label}</button>
          );})}
        </div>
      </div>
      {/* Cuisine Tags */}
      <div style={{marginBottom:14}}>
        <label style={LB({marginBottom:8})}>Cuisine / Region</label>
        <TagPicker items={CUISINES} selected={r.cuisineTags} onChange={v=>up("cuisineTags",v)} colorActive={C.blue}/>
      </div>
      {/* Occasion Tags */}
      <div style={{marginBottom:14}}>
        <label style={LB({marginBottom:8})}>Occasion Tags</label>
        <TagPicker items={OCCASIONS} selected={r.occasionTags} onChange={v=>up("occasionTags",v)}/>
      </div>
      {/* Text fields */}
      {[["Recipe Name","name",false],["Description","description",false],["Notes & Family Memories","notes",true]].map(([label,field,isArea])=>(
        <div key={field} style={{marginBottom:14}}>
          <label style={LB({marginBottom:5})}>{label}</label>
          {isArea?<textarea value={r[field]||""} onChange={e=>up(field,e.target.value)} style={{...IS,minHeight:60,resize:"vertical"}}/>
                 :<input value={r[field]||""} onChange={e=>up(field,e.target.value)} style={IS}/>}
        </div>
      ))}
      {/* Meta */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:14}}>
        {[["Servings","servings"],["Prep Time","prepTime"],["Cook Time","cookTime"]].map(([label,field])=>(
          <div key={field}><label style={LB({marginBottom:5})}>{label}</label><input value={r[field]||""} onChange={e=>up(field,e.target.value)} style={IS}/></div>
        ))}
      </div>
      {/* Ingredients */}
      <div style={{marginBottom:14}}>
        <label style={LB({marginBottom:8})}>Ingredients</label>
        {r.ingredients?.map((ing,i)=><input key={i} value={ing} onChange={e=>{const a=[...r.ingredients];a[i]=e.target.value;up("ingredients",a);}} style={{...IS,marginBottom:5}}/>)}
      </div>
      {/* Steps */}
      <div style={{marginBottom:14}}>
        <label style={LB({marginBottom:8})}>Steps</label>
        {r.steps?.map((step,i)=>(
          <div key={i} style={{display:"flex",gap:8,marginBottom:6}}>
            <span style={{fontFamily:"Playfair Display, serif",fontSize:14,color:C.amber,minWidth:22,paddingTop:9,fontWeight:700}}>{i+1}.</span>
            <textarea value={step} onChange={e=>{const a=[...r.steps];a[i]=e.target.value;up("steps",a);}} style={{...IS,resize:"vertical",minHeight:50,flex:1}}/>
          </div>
        ))}
      </div>
      {/* Photos */}
      <div style={{marginBottom:14}}>
        <label style={LB({marginBottom:4})}>Photos <span style={{fontStyle:"italic",fontWeight:400,textTransform:"none",letterSpacing:0,color:C.border}}>— optional</span></label>
        <PhotoUploader photos={r.photos||[]} onChange={v=>up("photos",v)}/>
      </div>
      {/* Video */}
      <div style={{marginBottom:14}}>
        <label style={LB({marginBottom:8})}>🎥 Video <span style={{fontStyle:"italic",fontWeight:400,textTransform:"none",letterSpacing:0,color:C.border}}>— optional</span></label>
        <input value={r.videoUrl||""} onChange={e=>up("videoUrl",e.target.value)} placeholder="Paste YouTube / video link…" style={{...IS,marginBottom:8}} disabled={!!r.videoFile}/>
        {!r.videoUrl&&<VideoUploader videoFile={r.videoFile} onChange={v=>up("videoFile",v)}/>}
      </div>
      {/* Public toggle */}
      <div style={{marginBottom:20,display:"flex",alignItems:"center",gap:12,background:`${C.blue}10`,border:`1px solid ${C.blue}30`,borderRadius:9,padding:"10px 14px"}}>
        <button onClick={()=>up("isPublic",!r.isPublic)} style={{width:44,height:24,borderRadius:12,background:r.isPublic?C.blue:C.border,border:"none",cursor:"pointer",position:"relative",transition:"background .2s"}}>
          <span style={{position:"absolute",top:2,left:r.isPublic?22:2,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"left .2s",display:"block"}}/>
        </button>
        <div>
          <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.text,margin:0,fontWeight:600}}>{r.isPublic?"🌐 Public recipe":"🔒 Private recipe"}</p>
          <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,margin:0}}>{r.isPublic?"Shareable with anyone via link":"Visible only within your family vault"}</p>
        </div>
      </div>
      <div style={{display:"flex",gap:10,marginTop:22}}>
        <button onClick={onBack} style={{flex:1,padding:"11px 0",background:"none",border:`1.5px solid ${C.border}`,borderRadius:8,fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.muted,cursor:"pointer"}}>← Back</button>
        <button onClick={()=>{setSaving(true);onConfirm(r);}} disabled={saving} style={{flex:2,padding:"11px 0",background:`linear-gradient(135deg,${C.green},#2D6040)`,color:"#fff",border:"none",borderRadius:8,fontFamily:"Playfair Display, serif",fontSize:16,cursor:saving?"default":"pointer",opacity:saving?.8:1}}>
          {saving?"Saving…":"💾 Save to Vault"}
        </button>
      </div>
    </div>
  );
}

// ─── RECIPE DETAIL ────────────────────────────────────────────────────────────
function RecipeDetail({recipe,onClose,onDelete,onUpdate,onAdapt,allRecipes,identity}){
  const [confirmDelete,setConfirmDelete]=useState(false);
  const [scale,setScale]=useState(1);
  const [activePhoto,setActivePhoto]=useState(0);
  const [editMode,setEditMode]=useState(false);
  const [editData,setEditData]=useState(null);
  const [saving,setSaving]=useState(false);
  const di=DIETARY.find(d=>d.id===recipe.dietaryType);
  const typeLabel={voice:"🎙️ Voice Dictated",scan:"📷 Scanned",manual:"✍️ Typed"}[recipe.inputType]||"";
  const allPhotos=[recipe.imageBase64,...(recipe.photos||[])].filter(Boolean);
  const SCALES=[0.5,1,2,3,4];
  const userCanEdit=canEdit(identity,recipe);
  const userCanDelete=canDelete(identity);

  const startEdit=()=>{setEditData({...recipe});setEditMode(true);};
  const upEdit=(f,v)=>setEditData(p=>({...p,[f]:v}));
  const saveEdit=async()=>{setSaving(true);await onUpdate(editData);setSaving(false);setEditMode(false);};

  const getEmbed=url=>{if(!url)return null;const yt=url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);return yt?`https://www.youtube.com/embed/${yt[1]}`:null;};
  const embedUrl=getEmbed(recipe.videoUrl);
  const hasVideo=embedUrl||recipe.videoUrl||recipe.videoFile;

  const originalRecipe=recipe.adaptedFrom?allRecipes?.find(r=>r.id===recipe.adaptedFrom.id):null;

  return(
    <div style={{position:"fixed",inset:0,background:C.overlay,zIndex:200,overflowY:"auto"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.bg,maxWidth:660,margin:"0 auto",minHeight:"100vh",boxShadow:"0 0 50px rgba(0,0,0,.4)",animation:"fadeIn .25s ease"}}>
        {/* Hero */}
        <div style={{background:C.headerBg,padding:"28px 28px 24px",position:"relative"}}>
          <button onClick={onClose} style={{position:"absolute",top:18,right:18,background:"rgba(255,255,255,.15)",border:"none",borderRadius:"50%",width:36,height:36,cursor:"pointer",color:"#fff",fontSize:16}}>✕</button>
          <div style={{marginBottom:8,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:`${C.parchment}99`,textTransform:"uppercase",letterSpacing:1}}>{typeLabel}</span>
            {recipe.originalLanguage&&recipe.originalLanguage!=="English"&&<span style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:`${C.parchment}70`,fontStyle:"italic"}}>• Originally in {recipe.originalLanguage}</span>}
            {di&&<span style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,fontWeight:600,color:"#fff",background:`${di.color}90`,padding:"2px 10px",borderRadius:20}}>{di.emoji} {di.label}</span>}
            {recipe.isPublic&&<span style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:"#fff",background:`${C.blue}90`,padding:"2px 10px",borderRadius:20}}>🌐 Public</span>}
            <button onClick={()=>onUpdate({...recipe,isFavourite:!recipe.isFavourite})} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:20,padding:"2px 10px",cursor:"pointer",fontSize:13,color:"#fff"}}>{recipe.isFavourite?"❤️ Saved":"🤍 Favourite"}</button>
          </div>
          <h1 style={{fontFamily:"Playfair Display, serif",fontSize:30,color:C.parchment,margin:"0 0 8px",lineHeight:1.2,paddingRight:40}}>{recipe.name}</h1>
          {recipe.description&&<p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",color:`${C.parchment}CC`,fontSize:16,margin:"0 0 10px",lineHeight:1.6}}>{recipe.description}</p>}
          {recipe.contributor&&(
            <div style={{marginTop:6}}>
              <p style={{fontFamily:"Cormorant Garamond, serif",color:`${C.parchment}80`,fontSize:14,margin:0}}>
                Contributed by <strong style={{color:`${C.parchment}CC`}}>{recipe.contributor}</strong>
                {(recipe.contributorCity||recipe.contributorCountry)&&(
                  <span style={{color:`${C.parchment}70`,fontSize:13}}>
                    {" "}· 📍 {[recipe.contributorCity,recipe.contributorCountry].filter(Boolean).join(", ")}
                  </span>
                )}
              </p>
              {/* Mobile & email — Owner only */}
              {identity?.isOwner&&(recipe.contributorMobile||recipe.contributorEmail)&&(
                <div style={{marginTop:6,background:"rgba(0,0,0,0.2)",borderRadius:7,padding:"7px 12px",display:"inline-flex",flexWrap:"wrap",gap:14,alignItems:"center"}}>
                  <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:11,color:`${C.parchment}60`,textTransform:"uppercase",letterSpacing:1}}>🔒 Admin view</span>
                  {recipe.contributorMobile&&<span style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:`${C.parchment}CC`}}>📱 {recipe.contributorMobile}</span>}
                  {recipe.contributorEmail&&<span style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:`${C.parchment}CC`}}>✉️ {recipe.contributorEmail}</span>}
                </div>
              )}
            </div>
          )}
          {recipe.adaptedFrom&&<p style={{fontFamily:"Cormorant Garamond, serif",color:`${C.parchment}70`,fontSize:13,margin:"4px 0 0"}}>🔀 Adapted from <em>{recipe.adaptedFrom.name}</em> by {recipe.adaptedFrom.contributor}</p>}
          {(recipe.occasionTags?.length>0||recipe.cuisineTags?.length>0)&&(
            <div style={{display:"flex",flexWrap:"wrap",gap:6,marginTop:10}}>
              {(recipe.occasionTags||[]).map(ot=>{const o=OCCASIONS.find(x=>x.id===ot);return o?<span key={ot} style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,background:"rgba(255,255,255,.15)",color:"#fff",padding:"2px 10px",borderRadius:20}}>{o.emoji} {o.label}</span>:null;})}
              {(recipe.cuisineTags||[]).map(ct=>{const cu=CUISINES.find(x=>x.id===ct);return cu?<span key={ct} style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,background:`${C.blue}80`,color:"#fff",padding:"2px 10px",borderRadius:20}}>{cu.emoji} {cu.label}</span>:null;})}
            </div>
          )}
        </div>

        <div style={{padding:"24px 28px"}}>
          {/* Share buttons */}
          <div style={{display:"flex",gap:8,marginBottom:22,flexWrap:"wrap"}}>
            <button onClick={()=>shareRecipe(recipe)} style={{padding:"7px 14px",borderRadius:20,border:`1.5px solid ${C.border}`,background:"none",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted}}>📤 Share</button>
            <button onClick={()=>whatsappShare(recipe)} style={{padding:"7px 14px",borderRadius:20,border:"1.5px solid #25D366",background:"none",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:"#25D366"}}>💬 WhatsApp</button>
            <button onClick={()=>navigator.clipboard.writeText(window.location.href).then(()=>alert("Link copied!"))} style={{padding:"7px 14px",borderRadius:20,border:`1.5px solid ${C.border}`,background:"none",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted}}>🔗 Copy Link</button>
            <button onClick={()=>onUpdate({...recipe,isPublic:!recipe.isPublic})} style={{padding:"7px 14px",borderRadius:20,border:`1.5px solid ${recipe.isPublic?C.blue:C.border}`,background:recipe.isPublic?`${C.blue}15`:"none",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:recipe.isPublic?C.blue:C.muted}}>{recipe.isPublic?"🌐 Public":"🔒 Private"}</button>
            <button onClick={()=>onAdapt(recipe)} style={{padding:"7px 14px",borderRadius:20,border:`1.5px solid ${C.amber}`,background:`${C.amber}10`,cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.amber}}>🔀 Adapt Recipe</button>
          </div>

          {/* Photo Gallery */}
          {allPhotos.length>0&&(
            <div style={{marginBottom:26}}>
              <p style={SH}>📷 Photos</p>
              <img src={allPhotos[activePhoto]} alt="" style={{width:"100%",maxHeight:320,objectFit:"cover",borderRadius:9,border:`1px solid ${C.border}`}}/>
              {allPhotos.length>1&&<div style={{display:"flex",gap:8,marginTop:8,overflowX:"auto"}}>{allPhotos.map((p,i)=><img key={i} src={p} alt="" onClick={()=>setActivePhoto(i)} style={{width:60,height:60,objectFit:"cover",borderRadius:6,cursor:"pointer",border:`2px solid ${i===activePhoto?C.amber:C.border}`,flexShrink:0}}/>)}</div>}
            </div>
          )}

          {/* Video */}
          {hasVideo&&(
            <div style={{marginBottom:26}}>
              <p style={SH}>🎥 Recipe Video</p>
              {embedUrl&&<iframe src={embedUrl} title="video" width="100%" height="280" frameBorder="0" allowFullScreen style={{borderRadius:9,border:`1px solid ${C.border}`}}/>}
              {recipe.videoUrl&&!embedUrl&&<a href={recipe.videoUrl} target="_blank" rel="noopener noreferrer" style={{fontFamily:"Cormorant Garamond, serif",color:C.amber,fontSize:15}}>▶ Watch Video</a>}
              {recipe.videoFile&&!recipe.videoUrl&&<video src={recipe.videoFile} controls style={{width:"100%",borderRadius:9,border:`1px solid ${C.border}`,maxHeight:360}}/>}
            </div>
          )}

          {/* Meta */}
          {(recipe.prepTime||recipe.cookTime||recipe.servings)&&(
            <div style={{display:"flex",gap:14,marginBottom:16,flexWrap:"wrap"}}>
              {recipe.prepTime&&<div style={MB}><div style={ML}>Prep</div><div style={MV}>{recipe.prepTime}</div></div>}
              {recipe.cookTime&&<div style={MB}><div style={ML}>Cook</div><div style={MV}>{recipe.cookTime}</div></div>}
              {recipe.servings&&<div style={MB}><div style={ML}>Serves</div><div style={MV}>{recipe.servings}</div></div>}
            </div>
          )}

          {/* Scaler */}
          <div style={{background:`${C.amber}12`,border:`1px solid ${C.amber}30`,borderRadius:10,padding:"10px 16px",marginBottom:26,display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.brown,fontWeight:600}}>⚖️ Scale recipe:</span>
            {SCALES.map(s=><button key={s} onClick={()=>setScale(s)} style={{padding:"5px 14px",borderRadius:20,border:`1.5px solid ${scale===s?C.amber:C.border}`,background:scale===s?`${C.amber}20`:"transparent",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:14,color:scale===s?C.amber:C.muted,fontWeight:scale===s?700:400}}>{s===0.5?"½×":`${s}×`}</button>)}
          </div>

          {/* Voice note */}
          {recipe.audioBase64&&(
            <div style={{marginBottom:26,background:`${C.amber}12`,border:`1px solid ${C.amber}40`,borderRadius:11,padding:"16px 18px"}}>
              <p style={{fontFamily:"Playfair Display, serif",fontSize:15,color:C.brown,margin:"0 0 10px"}}>🎙️ Original Voice Note — Preserved Forever</p>
              <audio src={recipe.audioBase64} controls style={{width:"100%",borderRadius:7}}/>
              {recipe.rawTranscription&&<details style={{marginTop:10}}><summary style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,cursor:"pointer"}}>📝 View original transcription ▾</summary><p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.text,marginTop:8,lineHeight:1.7,fontStyle:"italic",borderLeft:`3px solid ${C.border}`,paddingLeft:12}}>{recipe.rawTranscription}</p></details>}
            </div>
          )}

          {/* Ingredients */}
          {recipe.ingredients?.length>0&&(
            <section style={{marginBottom:26}}>
              <h2 style={SH}>Ingredients {scale!==1&&<span style={{fontSize:14,color:C.amber,fontStyle:"italic"}}>({scale===0.5?"½":scale}× serving)</span>}</h2>
              <ul style={{listStyle:"none",padding:0,margin:0}}>
                {recipe.ingredients.map((ing,i)=><li key={i} style={{fontFamily:"Cormorant Garamond, serif",fontSize:16,color:C.text,padding:"9px 0",borderBottom:`1px solid ${C.parchment}`,display:"flex",gap:10,alignItems:"baseline"}}><span style={{color:C.amber,fontSize:11,flexShrink:0}}>◆</span>{scaleIngredient(ing,scale)}</li>)}
              </ul>
            </section>
          )}

          {/* Steps */}
          {recipe.steps?.length>0&&(
            <section style={{marginBottom:26}}>
              <h2 style={SH}>Method</h2>
              {recipe.steps.map((step,i)=><div key={i} style={{display:"flex",gap:14,marginBottom:16}}><div style={{minWidth:32,height:32,borderRadius:"50%",background:`linear-gradient(135deg,${C.amber},${C.sienna})`,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontFamily:"Playfair Display, serif",fontSize:14,fontWeight:700,flexShrink:0,marginTop:2}}>{i+1}</div><p style={{fontFamily:"Cormorant Garamond, serif",fontSize:16,color:C.text,margin:0,lineHeight:1.75,paddingTop:6}}>{step}</p></div>)}
            </section>
          )}

          {/* Notes */}
          {recipe.notes&&<div style={{background:`${C.amber}14`,border:`1px solid ${C.amber}45`,borderRadius:10,padding:"15px 17px",marginBottom:22}}><h3 style={{fontFamily:"Playfair Display, serif",fontSize:16,color:C.amber,margin:"0 0 8px"}}>💛 Notes & Family Memories</h3><p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",fontSize:15.5,color:C.text,margin:0,lineHeight:1.75}}>{recipe.notes}</p></div>}

          {/* Tags */}
          {recipe.tags?.length>0&&<div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:22}}>{recipe.tags.map(t=><span key={t} style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,background:C.tag,color:C.sienna,padding:"4px 13px",borderRadius:20,border:`1px solid ${C.border}`}}>{t}</span>)}</div>}

          {/* AI Story Mode */}
          <StorySection recipe={recipe} onUpdate={onUpdate}/>

          {/* Ratings */}
          <RatingsSection recipe={recipe} onUpdate={onUpdate} identity={identity}/>

          {/* Nutrition */}
          <NutritionSection recipe={recipe} onUpdate={onUpdate}/>

          {/* Comments */}
          <CommentsSection recipe={recipe} onUpdate={onUpdate}/>

          {/* Original recipe link */}
          {originalRecipe&&<div style={{background:`${C.amber}10`,border:`1px solid ${C.amber}30`,borderRadius:9,padding:"10px 14px",marginBottom:16}}><p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.amber,margin:0}}>🔀 This is an adaptation of <strong>{originalRecipe.name}</strong> by {originalRecipe.contributor}</p></div>}

          <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.border,textAlign:"center",margin:"0 0 16px"}}>Added {new Date(recipe.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"})}</p>

          <AddVideoSection recipe={recipe} onUpdate={onUpdate}/>

          {/* ── Edit panel (owner / contributor only) ── */}
          {userCanEdit&&!editMode&&(
            <button onClick={startEdit}
              style={{width:"100%",padding:"10px",background:`${C.amber}12`,border:`1.5px solid ${C.amber}50`,borderRadius:8,fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.amber,cursor:"pointer",marginBottom:10,fontWeight:600}}>
              ✏️ Edit This Recipe
            </button>
          )}

          {editMode&&editData&&(
            <div style={{background:C.parchment,border:`1.5px solid ${C.amber}`,borderRadius:12,padding:"18px 20px",marginBottom:14}}>
              <p style={{fontFamily:"Playfair Display, serif",fontSize:17,color:C.brown,margin:"0 0 16px",borderBottom:`1px solid ${C.border}`,paddingBottom:8}}>✏️ Editing Recipe</p>

              {[["Recipe Name","name",false],["Description","description",false],["Notes","notes",true]].map(([label,field,isArea])=>(
                <div key={field} style={{marginBottom:12}}>
                  <label style={LB({marginBottom:4})}>{label}</label>
                  {isArea
                    ?<textarea value={editData[field]||""} onChange={e=>upEdit(field,e.target.value)} style={{...IS,minHeight:60,resize:"vertical"}}/>
                    :<input value={editData[field]||""} onChange={e=>upEdit(field,e.target.value)} style={IS}/>}
                </div>
              ))}

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:12}}>
                {[["Servings","servings"],["Prep Time","prepTime"],["Cook Time","cookTime"]].map(([label,field])=>(
                  <div key={field}><label style={LB({marginBottom:4})}>{label}</label><input value={editData[field]||""} onChange={e=>upEdit(field,e.target.value)} style={IS}/></div>
                ))}
              </div>

              <div style={{marginBottom:12}}>
                <label style={LB({marginBottom:6})}>Dietary Type</label>
                <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                  {DIETARY.filter(d=>d.id!=="all").map(d=>{const active=editData.dietaryType===d.id;return(
                    <button key={d.id} onClick={()=>upEdit("dietaryType",d.id)} style={{padding:"5px 11px",borderRadius:20,border:`1.5px solid ${active?d.color:C.border}`,background:active?`${d.color}20`:"transparent",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:12,color:active?d.color:C.muted,fontWeight:active?600:400}}>{d.emoji} {d.label}</button>
                  );})}
                </div>
              </div>

              <div style={{marginBottom:12}}>
                <label style={LB({marginBottom:6})}>Occasion Tags</label>
                <TagPicker items={OCCASIONS} selected={editData.occasionTags||[]} onChange={v=>upEdit("occasionTags",v)}/>
              </div>

              <div style={{marginBottom:14}}>
                <label style={LB({marginBottom:6})}>Cuisine Tags</label>
                <TagPicker items={CUISINES} selected={editData.cuisineTags||[]} onChange={v=>upEdit("cuisineTags",v)} colorActive={C.blue}/>
              </div>

              <div style={{marginBottom:12}}>
                <label style={LB({marginBottom:6})}>Ingredients</label>
                {(editData.ingredients||[]).map((ing,i)=><input key={i} value={ing} onChange={e=>{const a=[...editData.ingredients];a[i]=e.target.value;upEdit("ingredients",a);}} style={{...IS,marginBottom:5}}/>)}
              </div>

              <div style={{marginBottom:16}}>
                <label style={LB({marginBottom:6})}>Steps</label>
                {(editData.steps||[]).map((step,i)=>(
                  <div key={i} style={{display:"flex",gap:8,marginBottom:6}}>
                    <span style={{fontFamily:"Playfair Display, serif",fontSize:13,color:C.amber,minWidth:20,paddingTop:9,fontWeight:700}}>{i+1}.</span>
                    <textarea value={step} onChange={e=>{const a=[...editData.steps];a[i]=e.target.value;upEdit("steps",a);}} style={{...IS,resize:"vertical",minHeight:50,flex:1}}/>
                  </div>
                ))}
              </div>

              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>setEditMode(false)} style={{flex:1,padding:"10px",background:"none",border:`1.5px solid ${C.border}`,borderRadius:8,fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,cursor:"pointer"}}>Cancel</button>
                <button onClick={saveEdit} disabled={saving} style={{flex:2,padding:"10px",background:`linear-gradient(135deg,${C.green},#2D6040)`,border:"none",borderRadius:8,fontFamily:"Playfair Display, serif",fontSize:15,color:"#fff",cursor:saving?"default":"pointer",opacity:saving?.8:1}}>
                  {saving?"Saving…":"💾 Save Changes"}
                </button>
              </div>
            </div>
          )}

          {/* ── Delete (owner only) ── */}
          {userCanDelete&&(
            !confirmDelete
              ?<button onClick={()=>setConfirmDelete(true)} style={{width:"100%",padding:"10px",background:"none",border:`1.5px solid ${C.border}`,borderRadius:8,fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,cursor:"pointer",marginBottom:10}}>🗑 Remove Recipe</button>
              :<div style={{background:`${C.red}10`,border:`1px solid ${C.red}40`,borderRadius:9,padding:14,textAlign:"center",marginBottom:10}}>
                <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.red,margin:"0 0 12px"}}>Permanently remove this recipe?</p>
                <div style={{display:"flex",gap:10}}>
                  <button onClick={()=>setConfirmDelete(false)} style={{flex:1,padding:"9px",background:"none",border:`1.5px solid ${C.border}`,borderRadius:7,fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,cursor:"pointer"}}>Cancel</button>
                  <button onClick={onDelete} style={{flex:1,padding:"9px",background:C.red,border:"none",borderRadius:7,fontFamily:"Cormorant Garamond, serif",fontSize:14,color:"#fff",cursor:"pointer"}}>Yes, Remove</button>
                </div>
              </div>
          )}

          {/* ── No edit access notice ── */}
          {!userCanEdit&&!userCanDelete&&(
            <div style={{background:`${C.parchment}`,border:`1px solid ${C.border}`,borderRadius:9,padding:"12px 16px",marginBottom:10,textAlign:"center"}}>
              <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,margin:0}}>
                🔒 Only <strong>{recipe.contributor}</strong> or the vault owner can edit or remove this recipe.<br/>
                Use <strong>💬 Comments</strong> above to suggest a change.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ADD RECIPE MODAL ─────────────────────────────────────────────────────────
function AddRecipeModal({onClose,onSaved,adaptFrom=null}){
  const [tab,setTab]=useState("voice");
  const [stage,setStage]=useState("input");
  const [structured,setStructured]=useState(null);
  const [contributor,setContributor]=useState("");
  const [contribCity,setContribCity]=useState("");
  const [contribCountry,setContribCountry]=useState("");
  const [contribMobile,setContribMobile]=useState("");
  const [contribEmail,setContribEmail]=useState("");
  const [contribError,setContribError]=useState("");

  const handleStructured=recipe=>{
    const name=contributor.trim();
    if(!name||name.toLowerCase()==="anonymous"){setContribError("Please enter your real name — 'Anonymous' is not allowed.");return;}
    if(!contribCity.trim()||!contribCountry.trim()){setContribError("City and country are required.");return;}
    if(!contribMobile.trim()){setContribError("Mobile number is required.");return;}
    if(!contribEmail.trim()){setContribError("Email address is required.");return;}
    setContribError("");
    const base=adaptFrom?{...recipe,adaptedFrom:{id:adaptFrom.id,name:adaptFrom.name,contributor:adaptFrom.contributor}}:recipe;
    setStructured(base);setStage("review");
  };
  const handleConfirm=async recipe=>{
    const id=`${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const final={...recipe,id,
      contributor:contributor.trim(),
      contributorCity:contribCity.trim(),
      contributorCountry:contribCountry.trim(),
      contributorMobile:contribMobile.trim(),
      contributorEmail:contribEmail.trim(),
      createdAt:new Date().toISOString(),isFavourite:false,comments:[],isPublic:recipe.isPublic||false};
    await persistRecipe(final);
    onSaved(final);onClose();
  };

  return(
    <div style={{position:"fixed",inset:0,background:C.overlay,zIndex:100,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"20px 14px",overflowY:"auto"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.bg,borderRadius:16,width:"100%",maxWidth:530,boxShadow:"0 24px 70px rgba(0,0,0,.35)",marginTop:10,marginBottom:20,animation:"fadeIn .25s ease"}}>
        <div style={{background:C.parchment,padding:"18px 24px",borderRadius:"16px 16px 0 0",display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`1px solid ${C.border}`}}>
          <h2 style={{fontFamily:"Playfair Display, serif",fontSize:20,color:C.brown,margin:0}}>
            {adaptFrom?`🔀 Adapt "${adaptFrom.name}"`:stage==="review"?"✏️ Review Before Saving":"📖 Add a Recipe"}
          </h2>
          <button onClick={onClose} style={{background:"none",border:"none",fontSize:20,cursor:"pointer",color:C.muted,lineHeight:1}}>✕</button>
        </div>
        <div style={{padding:24}}>
          {adaptFrom&&stage==="input"&&<div style={{background:`${C.amber}15`,border:`1px solid ${C.amber}40`,borderRadius:8,padding:"10px 14px",marginBottom:16}}><p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.amber,margin:0}}>🔀 You are adapting <strong>{adaptFrom.name}</strong>. Add your version — original will be credited.</p></div>}
          {stage==="input"?(
            <>
              {/* ── Contributor details ── */}
              <div style={{marginBottom:20}}>

                {/* Privacy reassurance + vault creator context */}
                <div style={{background:`${C.green}12`,border:`1px solid ${C.green}35`,borderRadius:9,padding:"11px 14px",marginBottom:16,display:"flex",gap:10,alignItems:"flex-start"}}>
                  <span style={{fontSize:18,flexShrink:0}}>🔒</span>
                  <div>
                    <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13.5,color:C.green,margin:"0 0 6px",lineHeight:1.65}}>
                      <strong>Your privacy is protected.</strong> Your mobile number and email address are collected only for the vault administrator's records and will <strong>never be shown to other family members or visitors.</strong> Only your name, city and country will be visible to others.
                    </p>
                    <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.green,margin:0,lineHeight:1.6,opacity:.85}}>
                      📜 This vault was built by <strong>Ritesh Shah</strong> (youngest son of Mulchand Shah, husband of Neeta Shah). Use this as your reference when describing your connection to the family below.
                    </p>
                  </div>
                </div>

                <label style={LB({marginBottom:6})}>Your Name <span style={{color:C.red}}>*</span></label>
                <input value={contributor} onChange={e=>{setContributor(e.target.value);setContribError("");}}
                  placeholder="e.g. Grandma Lakshmi, Nani Ji, Ammamma…"
                  style={{...IS,marginBottom:12,borderColor:contribError?C.red:C.border}}/>

                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:12}}>
                  <div>
                    <label style={LB({marginBottom:6})}>City <span style={{color:C.red}}>*</span></label>
                    <input value={contribCity} onChange={e=>{setContribCity(e.target.value);setContribError("");}}
                      placeholder="e.g. Mumbai, Delhi…" style={{...IS}}/>
                  </div>
                  <div>
                    <label style={LB({marginBottom:6})}>Country <span style={{color:C.red}}>*</span></label>
                    <input value={contribCountry} onChange={e=>{setContribCountry(e.target.value);setContribError("");}}
                      placeholder="e.g. India, UK…" style={{...IS}}/>
                  </div>
                </div>

                <label style={LB({marginBottom:6})}>
                  Mobile Number <span style={{color:C.red}}>*</span>
                  <span style={{color:C.green,fontWeight:600,marginLeft:6,textTransform:"none",letterSpacing:0}}>🔒 Admin only</span>
                </label>
                <input value={contribMobile} onChange={e=>{setContribMobile(e.target.value);setContribError("");}}
                  placeholder="+91 98765 43210" style={{...IS,marginBottom:12}}/>

                <label style={LB({marginBottom:6})}>
                  Email Address <span style={{color:C.red}}>*</span>
                  <span style={{color:C.green,fontWeight:600,marginLeft:6,textTransform:"none",letterSpacing:0}}>🔒 Admin only</span>
                </label>
                <input type="email" value={contribEmail} onChange={e=>{setContribEmail(e.target.value);setContribError("");}}
                  placeholder="grandma@family.com" style={{...IS}}/>

                {contribError&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.red,margin:"8px 0 0"}}>{contribError}</p>}
              </div>
              <div style={{display:"flex",borderBottom:`2px solid ${C.border}`,marginBottom:22}}>
                {[{id:"voice",icon:"🎙️",label:"Voice"},{id:"scan",icon:"📷",label:"Scan"},{id:"manual",icon:"✍️",label:"Type"}].map(t=>(
                  <button key={t.id} onClick={()=>setTab(t.id)} style={{flex:1,padding:"10px 6px",border:"none",background:"none",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:15,color:tab===t.id?C.amber:C.muted,borderBottom:tab===t.id?`2.5px solid ${C.amber}`:"2.5px solid transparent",marginBottom:-2,fontWeight:tab===t.id?600:400}}>
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
              {tab==="voice"&&<VoiceTab onStructured={handleStructured}/>}
              {tab==="scan"&&<ScanTab onStructured={handleStructured}/>}
              {tab==="manual"&&<TypeTab onStructured={handleStructured}/>}
            </>
          ):(
            <RecipePreview recipe={structured} onConfirm={handleConfirm} onBack={()=>setStage("input")}/>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CHEF PROFILES ────────────────────────────────────────────────────────────
function ChefProfiles({recipes,onClose,onSelectContributor,identity}){
  const [profiles,setProfiles]=useState(loadProfiles());
  const [editing,setEditing]=useState(null);
  const [bioText,setBioText]=useState("");

  const contributors=[...new Set(recipes.map(r=>r.contributor).filter(Boolean))].sort();

  const saveProfile=(name,bio)=>{
    const updated={...profiles,[name]:{...(profiles[name]||{}),bio,updatedAt:new Date().toISOString()}};
    setProfiles(updated);saveProfiles(updated);setEditing(null);
  };

  return(
    <div style={{position:"fixed",inset:0,background:C.overlay,zIndex:150,overflowY:"auto"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.bg,maxWidth:660,margin:"0 auto",minHeight:"100vh",boxShadow:"0 0 50px rgba(0,0,0,.4)",animation:"fadeIn .25s ease"}}>
        <div style={{background:C.headerBg,padding:"24px 28px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <h2 style={{fontFamily:"Playfair Display, serif",fontSize:26,color:C.parchment,margin:0}}>👨‍🍳 Chef Profiles</h2>
            <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",color:`${C.parchment}80`,fontSize:14,margin:"4px 0 0"}}>The people behind the recipes</p>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:"50%",width:36,height:36,cursor:"pointer",color:"#fff",fontSize:16}}>✕</button>
        </div>
        <div style={{padding:"24px 28px"}}>
          {contributors.length===0&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:16,color:C.muted,fontStyle:"italic"}}>No contributors yet. Add some recipes to see chef profiles here.</p>}
          {contributors.map(name=>{
            const theirRecipes=recipes.filter(r=>r.contributor===name);
            const profile=profiles[name];
            const isEditing=editing===name;
            const ref=theirRecipes[0]||{};
            const city=ref.contributorCity;
            const country=ref.contributorCountry;
            const mobile=ref.contributorMobile;
            const email=ref.contributorEmail;
            return(
              <div key={name} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:13,padding:"20px 22px",marginBottom:16,animation:"fadeIn .3s ease"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10}}>
                  <div style={{display:"flex",gap:14,alignItems:"center"}}>
                    <div style={{width:52,height:52,borderRadius:"50%",background:`linear-gradient(135deg,${C.amber},${C.sienna})`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,flexShrink:0}}>👤</div>
                    <div>
                      <h3 style={{fontFamily:"Playfair Display, serif",fontSize:20,color:C.brown,margin:0}}>{name}</h3>
                      <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,margin:"2px 0 0"}}>
                        📖 {theirRecipes.length} recipe{theirRecipes.length!==1?"s":""} contributed
                        {(city||country)&&<span style={{marginLeft:8}}>· 📍 {[city,country].filter(Boolean).join(", ")}</span>}
                      </p>
                    </div>
                  </div>
                  <button onClick={()=>{setEditing(isEditing?null:name);setBioText(profile?.bio||"");}}
                    style={{background:"none",border:`1px solid ${C.border}`,borderRadius:7,padding:"5px 12px",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,cursor:"pointer"}}>
                    {isEditing?"Cancel":"✏️ Edit"}
                  </button>
                </div>

                {/* Admin-only contact info */}
                {identity?.isOwner&&(mobile||email)&&(
                  <div style={{background:`${C.green}10`,border:`1px solid ${C.green}30`,borderRadius:8,padding:"8px 14px",marginBottom:12,display:"flex",flexWrap:"wrap",gap:16,alignItems:"center"}}>
                    <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:11,color:C.green,textTransform:"uppercase",letterSpacing:1,fontWeight:700}}>🔒 Admin Contact</span>
                    {mobile&&<span style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.text}}>📱 {mobile}</span>}
                    {email&&<a href={`mailto:${email}`} style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.blue,textDecoration:"none"}}>✉️ {email}</a>}
                  </div>
                )}

                {isEditing?(
                  <div>
                    <label style={LB({marginBottom:5})}>Bio / About</label>
                    <textarea value={bioText} onChange={e=>setBioText(e.target.value)}
                      placeholder={`Write a short bio for ${name}…`}
                      style={{...IS,minHeight:80,resize:"vertical",marginBottom:8}}/>
                    <button onClick={()=>saveProfile(name,bioText)}
                      style={{...BP,width:"auto",padding:"8px 22px",fontSize:14}}>💾 Save</button>
                  </div>
                ):(
                  profile?.bio
                    ?<p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",fontSize:15,color:C.text,margin:"0 0 14px",lineHeight:1.65}}>{profile.bio}</p>
                    :<p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",fontSize:14,color:C.border,margin:"0 0 14px"}}>No bio yet — click Edit to add one.</p>
                )}

                {/* Mini recipe grid */}
                <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(120px,1fr))",gap:8}}>
                  {theirRecipes.slice(0,6).map(r=>(
                    <div key={r.id} onClick={()=>{onSelectContributor(name);onClose();}}
                      style={{background:C.parchment,borderRadius:8,padding:"8px 10px",cursor:"pointer",border:`1px solid ${C.border}`}}>
                      {(r.photos?.[0]||r.imageBase64)&&<img src={r.photos?.[0]||r.imageBase64} alt="" style={{width:"100%",height:60,objectFit:"cover",borderRadius:5,marginBottom:5}}/>}
                      <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.brown,margin:0,fontWeight:600,lineHeight:1.3}}>{r.name}</p>
                    </div>
                  ))}
                </div>
                {theirRecipes.length>6&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.amber,marginTop:8,cursor:"pointer"}} onClick={()=>{onSelectContributor(name);onClose();}}>View all {theirRecipes.length} recipes →</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── MEAL PLANNER ────────────────────────────────────────────────────────────
function MealPlanner({recipes,onClose}){
  const [plan,setPlan]=useState(loadMealPlan());
  const [picking,setPicking]=useState(null);
  const [search,setSearch]=useState("");
  const [weekOffset,setWeekOffset]=useState(0);
  const [showGrocery,setShowGrocery]=useState(false);

  // Get Monday of current week + offset
  const getWeekDates=()=>{
    const now=new Date();
    const day=now.getDay();
    const monday=new Date(now);
    monday.setDate(now.getDate()-(day===0?6:day-1)+(weekOffset*7));
    return DAYS.map((_,i)=>{const d=new Date(monday);d.setDate(monday.getDate()+i);return d;});
  };
  const dates=getWeekDates();
  const weekKey=dates[0].toISOString().slice(0,10);

  const getSlot=(day,meal)=>plan[`${weekKey}_${day}_${meal}`];
  const setSlot=(day,meal,recipeId)=>{
    const updated={...plan,[`${weekKey}_${day}_${meal}`]:recipeId};
    setPlan(updated);saveMealPlan(updated);
  };
  const clearSlot=(day,meal)=>{
    const updated={...plan};
    delete updated[`${weekKey}_${day}_${meal}`];
    setPlan(updated);saveMealPlan(updated);
  };

  const filteredRecipes=recipes.filter(r=>r.name.toLowerCase().includes(search.toLowerCase())||r.contributor?.toLowerCase().includes(search.toLowerCase()));

  return(
    <div style={{position:"fixed",inset:0,background:C.overlay,zIndex:150,overflowY:"auto"}}>
      <div style={{background:C.bg,maxWidth:900,margin:"0 auto",minHeight:"100vh",boxShadow:"0 0 50px rgba(0,0,0,.4)",animation:"fadeIn .25s ease"}}>
        <div style={{background:C.headerBg,padding:"24px 28px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <h2 style={{fontFamily:"Playfair Display, serif",fontSize:26,color:C.parchment,margin:0}}>🗓️ Weekly Meal Planner</h2>
            <div style={{display:"flex",gap:16,alignItems:"center",marginTop:8}}>
              <button onClick={()=>setWeekOffset(w=>w-1)} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:7,padding:"4px 12px",cursor:"pointer",color:"#fff",fontSize:16}}>‹</button>
              <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:`${C.parchment}CC`}}>
                {dates[0].toLocaleDateString("en-IN",{day:"numeric",month:"short"})} – {dates[6].toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}
              </span>
              <button onClick={()=>setWeekOffset(w=>w+1)} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:7,padding:"4px 12px",cursor:"pointer",color:"#fff",fontSize:16}}>›</button>
              {weekOffset!==0&&<button onClick={()=>setWeekOffset(0)} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",color:"#fff",fontSize:12,fontFamily:"Cormorant Garamond, serif"}}>Today</button>}
            </div>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:"50%",width:36,height:36,cursor:"pointer",color:"#fff",fontSize:16}}>✕</button>
        </div>

        <div style={{padding:"12px 20px 0",display:"flex",justifyContent:"flex-end"}}>
          <button onClick={()=>setShowGrocery(true)}
            style={{background:`${C.green}18`,border:`1px solid ${C.green}50`,borderRadius:8,padding:"8px 18px",fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.green,cursor:"pointer",fontWeight:600}}>
            🛒 Grocery List for This Week
          </button>
        </div>

        <div style={{padding:"20px 16px",overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",minWidth:600}}>
            <thead>
              <tr>
                <th style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,textTransform:"uppercase",letterSpacing:.8,padding:"8px 10px",textAlign:"left",width:80}}>Meal</th>
                {dates.map((d,i)=>(
                  <th key={i} style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:d.toDateString()===new Date().toDateString()?C.amber:C.brown,padding:"8px 6px",textAlign:"center",fontWeight:600}}>
                    <div>{DAYS[i]}</div>
                    <div style={{fontSize:11,fontWeight:400,color:C.muted}}>{d.getDate()}/{d.getMonth()+1}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MEALS.map(meal=>(
                <tr key={meal}>
                  <td style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,padding:"6px 10px",borderTop:`1px solid ${C.border}`,verticalAlign:"middle",whiteSpace:"nowrap"}}>{meal}</td>
                  {DAYS.map((day,i)=>{
                    const recipeId=getSlot(day,meal);
                    const recipe=recipeId?recipes.find(r=>r.id===recipeId):null;
                    return(
                      <td key={i} className="meal-cell" onClick={()=>setPicking({day,meal})}
                        style={{border:`1px solid ${C.border}`,padding:"6px",textAlign:"center",verticalAlign:"middle",minWidth:90,height:64,background:recipe?`${C.amber}10`:C.card,position:"relative",transition:"background .15s"}}>
                        {recipe?(
                          <div>
                            <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:11,color:C.brown,margin:0,fontWeight:600,lineHeight:1.3}}>{recipe.name}</p>
                            <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:10,color:C.muted,margin:0}}>{recipe.contributor}</p>
                            <button onClick={e=>{e.stopPropagation();clearSlot(day,meal);}} style={{position:"absolute",top:2,right:2,background:"none",border:"none",fontSize:10,cursor:"pointer",color:C.muted,lineHeight:1}}>✕</button>
                          </div>
                        ):(
                          <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:11,color:C.border}}>+ Add</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Recipe picker panel */}
        {picking&&(
          <div style={{position:"fixed",bottom:0,left:0,right:0,background:C.card,borderTop:`2px solid ${C.border}`,padding:"16px 24px",maxHeight:"50vh",overflowY:"auto",boxShadow:"0 -8px 32px rgba(0,0,0,.2)",zIndex:10}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <p style={{fontFamily:"Playfair Display, serif",fontSize:16,color:C.brown,margin:0}}>Pick a recipe for {picking.meal} on {picking.day}</p>
              <button onClick={()=>setPicking(null)} style={{background:"none",border:"none",fontSize:18,cursor:"pointer",color:C.muted}}>✕</button>
            </div>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search recipes…" style={{...IS,marginBottom:12}}/>
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))",gap:8}}>
              {filteredRecipes.map(r=>(
                <div key={r.id} onClick={()=>{setSlot(picking.day,picking.meal,r.id);setPicking(null);setSearch("");}}
                  style={{background:C.parchment,border:`1px solid ${C.border}`,borderRadius:9,padding:"10px 12px",cursor:"pointer",transition:"all .15s"}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=C.amber}
                  onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                  <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.brown,margin:0,fontWeight:600}}>{r.name}</p>
                  <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:11,color:C.muted,margin:"2px 0 0"}}>{r.contributor}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Grocery List */}
        {showGrocery&&(
          <GroceryListModal
            recipes={recipes}
            plan={plan}
            weekKey={weekKey}
            onClose={()=>setShowGrocery(false)}
          />
        )}
      </div>
    </div>
  );
}

// ─── AI STORY MODE ────────────────────────────────────────────────────────────
function StorySection({recipe,onUpdate}){
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState("");

  const generate=async()=>{
    setLoading(true);setError("");
    try{
      const story=await generateStory(recipe);
      onUpdate({...recipe,storyMode:story});
    }catch(e){setError("Could not generate story. Please try again.");}
    setLoading(false);
  };

  return(
    <div style={{marginBottom:24}}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <h2 style={SH}>🗣️ Family Story</h2>
        <button onClick={generate} disabled={loading}
          style={{background:`${C.amber}15`,border:`1px solid ${C.amber}50`,borderRadius:8,padding:"6px 14px",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.amber,cursor:"pointer",fontWeight:600}}>
          {loading?"Composing…":recipe.storyMode?"↺ Regenerate":"✨ Generate Story"}
        </button>
      </div>
      {recipe.storyMode?(
        <div style={{background:`${C.green}08`,border:`1px solid ${C.green}30`,borderRadius:10,padding:"15px 18px"}}>
          <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",fontSize:16,color:C.text,margin:0,lineHeight:1.85}}>"{recipe.storyMode}"</p>
        </div>
      ):(
        <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.muted,fontStyle:"italic",margin:0}}>
          Let AI weave a short narrative about this recipe's origin and family significance.
        </p>
      )}
      {error&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.red,marginTop:8}}>{error}</p>}
    </div>
  );
}

// ─── RATINGS ─────────────────────────────────────────────────────────────────
function RatingsSection({recipe,onUpdate,identity}){
  const ratings=recipe.ratings||[];
  const avg=ratings.length?Math.round((ratings.reduce((s,r)=>s+r.stars,0)/ratings.length)*10)/10:null;
  const myRating=identity?ratings.find(r=>r.name===identity.name):null;
  const [hover,setHover]=useState(0);

  const rate=stars=>{
    if(!identity){alert("Please identify yourself first.");return;}
    const updated=myRating
      ?ratings.map(r=>r.name===identity.name?{...r,stars,date:new Date().toISOString()}:r)
      :[...ratings,{name:identity.name,stars,date:new Date().toISOString()}];
    onUpdate({...recipe,ratings:updated});
  };

  return(
    <div style={{marginBottom:24}}>
      <h2 style={SH}>⭐ Family Ratings</h2>
      <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
        <div style={{display:"flex",gap:4}}>
          {[1,2,3,4,5].map(s=>(
            <span key={s} onMouseEnter={()=>setHover(s)} onMouseLeave={()=>setHover(0)}
              onClick={()=>rate(s)}
              style={{fontSize:26,cursor:"pointer",transition:"transform .1s",transform:(hover||myRating?.stars||0)>=s?"scale(1.15)":"scale(1)",filter:(hover||myRating?.stars||0)>=s?"none":"grayscale(1) opacity(.4)"}}>
              ⭐
            </span>
          ))}
        </div>
        {avg&&<span style={{fontFamily:"Playfair Display, serif",fontSize:18,color:C.amber}}>{avg} <span style={{fontSize:13,color:C.muted,fontFamily:"Cormorant Garamond, serif"}}>/ 5 · {ratings.length} rating{ratings.length!==1?"s":""}</span></span>}
        {myRating&&<span style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.green}}>✓ You rated {myRating.stars}/5</span>}
        {!myRating&&identity&&<span style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,fontStyle:"italic"}}>Tap a star to rate</span>}
      </div>
      {ratings.length>0&&(
        <div style={{marginTop:10,display:"flex",flexWrap:"wrap",gap:6}}>
          {ratings.map(r=>(
            <span key={r.name} style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,background:C.parchment,border:`1px solid ${C.border}`,borderRadius:20,padding:"3px 10px",color:C.muted}}>
              {r.name} — {"⭐".repeat(r.stars)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── GROCERY LIST ─────────────────────────────────────────────────────────────
function GroceryListModal({recipes,plan,weekKey,onClose}){
  const [grouped,setGrouped]=useState(null);
  const [loading,setLoading]=useState(false);
  const [checked,setChecked]=useState({});

  // Collect all ingredients from planned recipes this week
  const plannedIds=Object.entries(plan)
    .filter(([k])=>k.startsWith(weekKey))
    .map(([,id])=>id).filter(Boolean);
  const unique=[...new Set(plannedIds)];
  const plannedRecipes=unique.map(id=>recipes.find(r=>r.id===id)).filter(Boolean);
  const allIngredients=plannedRecipes.flatMap(r=>(r.ingredients||[]).map(i=>`${i} (${r.name})`));

  useEffect(()=>{
    if(allIngredients.length===0)return;
    setLoading(true);
    categorizeGrocery(allIngredients).then(g=>{setGrouped(g);setLoading(false);}).catch(()=>{
      setGrouped({"All Ingredients":allIngredients});setLoading(false);
    });
  },[]);

  const toggle=item=>setChecked(c=>({...c,[item]:!c[item]}));

  return(
    <div style={{position:"fixed",inset:0,background:C.overlay,zIndex:400,display:"flex",alignItems:"flex-start",justifyContent:"center",padding:"20px 14px",overflowY:"auto"}}>
      <div style={{background:C.bg,borderRadius:16,width:"100%",maxWidth:500,boxShadow:"0 24px 70px rgba(0,0,0,.35)",marginTop:10,marginBottom:20,animation:"fadeIn .25s ease"}}>
        <div style={{background:C.headerBg,padding:"20px 24px",borderRadius:"16px 16px 0 0",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <h2 style={{fontFamily:"Playfair Display, serif",fontSize:22,color:C.parchment,margin:0}}>🛒 Grocery List</h2>
            <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:`${C.parchment}80`,margin:"4px 0 0"}}>{plannedRecipes.length} recipes this week</p>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:"50%",width:36,height:36,cursor:"pointer",color:"#fff",fontSize:16}}>✕</button>
        </div>
        <div style={{padding:"20px 24px"}}>
          {plannedRecipes.length===0&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.muted,fontStyle:"italic"}}>No recipes planned this week yet. Fill in the planner first.</p>}
          {loading&&<Spinner label="Categorising ingredients…"/>}
          {grouped&&!loading&&(
            <>
              <div style={{marginBottom:14,padding:"8px 12px",background:`${C.amber}12`,borderRadius:8,display:"flex",gap:8,flexWrap:"wrap"}}>
                {plannedRecipes.map(r=><span key={r.id} style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,background:C.card,border:`1px solid ${C.border}`,borderRadius:20,padding:"2px 10px",color:C.brown}}>{r.name}</span>)}
              </div>
              {Object.entries(grouped).filter(([,items])=>items.length>0).map(([cat,items])=>(
                <div key={cat} style={{marginBottom:16}}>
                  <h3 style={{fontFamily:"Playfair Display, serif",fontSize:15,color:C.brown,margin:"0 0 8px",borderBottom:`1px solid ${C.border}`,paddingBottom:4}}>{cat}</h3>
                  {items.map(item=>(
                    <div key={item} onClick={()=>toggle(item)}
                      style={{display:"flex",gap:10,alignItems:"flex-start",padding:"5px 0",cursor:"pointer",borderBottom:`1px solid ${C.parchment}`}}>
                      <div style={{width:18,height:18,border:`2px solid ${checked[item]?C.green:C.border}`,borderRadius:4,background:checked[item]?C.green:"transparent",flexShrink:0,marginTop:2,display:"flex",alignItems:"center",justifyContent:"center",transition:"all .15s"}}>
                        {checked[item]&&<span style={{color:"#fff",fontSize:11}}>✓</span>}
                      </div>
                      <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:checked[item]?C.muted:C.text,textDecoration:checked[item]?"line-through":"none",lineHeight:1.5}}>{item}</span>
                    </div>
                  ))}
                </div>
              ))}
              <button onClick={()=>{const text=Object.entries(grouped).filter(([,v])=>v.length>0).map(([cat,items])=>`${cat}:\n`+items.map(i=>`• ${i}`).join("\n")).join("\n\n");navigator.clipboard.writeText(text).then(()=>alert("Grocery list copied!"));}}
                style={{...BP,marginTop:8,fontSize:14}}>
                📋 Copy Full List to Clipboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ANALYTICS DASHBOARD ──────────────────────────────────────────────────────
function AnalyticsDashboard({recipes,onClose}){
  const total=recipes.length;
  const favs=recipes.filter(r=>r.isFavourite).length;
  const withVoice=recipes.filter(r=>r.audioBase64).length;
  const withPhotos=recipes.filter(r=>r.photos?.length||r.imageBase64).length;
  const totalComments=recipes.reduce((s,r)=>s+(r.comments?.length||0),0);
  const adapted=recipes.filter(r=>r.adaptedFrom).length;
  const totalRatings=recipes.reduce((s,r)=>s+(r.ratings?.length||0),0);

  const topRated=[...recipes].filter(r=>r.ratings?.length>=1)
    .sort((a,b)=>(b.ratings.reduce((s,r)=>s+r.stars,0)/b.ratings.length)-(a.ratings.reduce((s,r)=>s+r.stars,0)/a.ratings.length))
    .slice(0,3);
  const mostCommented=[...recipes].sort((a,b)=>(b.comments?.length||0)-(a.comments?.length||0)).slice(0,3);
  const mostAdapted=recipes.filter(r=>r.id).map(r=>({...r,adaptCount:recipes.filter(x=>x.adaptedFrom?.id===r.id).length})).sort((a,b)=>b.adaptCount-a.adaptCount).filter(r=>r.adaptCount>0).slice(0,3);

  // By contributor
  const byContrib=[...new Set(recipes.map(r=>r.contributor).filter(Boolean))].map(name=>({name,count:recipes.filter(r=>r.contributor===name).length})).sort((a,b)=>b.count-a.count);
  const maxCount=byContrib[0]?.count||1;

  // By country
  const byCountry={};
  recipes.forEach(r=>{if(r.contributorCountry){byCountry[r.contributorCountry]=(byCountry[r.contributorCountry]||0)+1;}});
  const countryList=Object.entries(byCountry).sort((a,b)=>b[1]-a[1]);

  // By month added
  const byMonth=Array(12).fill(0);
  recipes.forEach(r=>{if(r.createdAt)byMonth[new Date(r.createdAt).getMonth()]++;});
  const months=["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const maxMonth=Math.max(...byMonth,1);

  return(
    <div style={{position:"fixed",inset:0,background:C.overlay,zIndex:300,overflowY:"auto"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.bg,maxWidth:720,margin:"0 auto",minHeight:"100vh",boxShadow:"0 0 50px rgba(0,0,0,.4)",animation:"fadeIn .25s ease"}}>
        <div style={{background:C.headerBg,padding:"24px 28px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <h2 style={{fontFamily:"Playfair Display, serif",fontSize:26,color:C.parchment,margin:0}}>📊 Recipe Analytics</h2>
            <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",color:`${C.parchment}80`,fontSize:14,margin:"4px 0 0"}}>A window into your family's culinary legacy</p>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:"50%",width:36,height:36,cursor:"pointer",color:"#fff",fontSize:16}}>✕</button>
        </div>
        <div style={{padding:"24px 28px"}}>

          {/* Summary cards */}
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(130px,1fr))",gap:12,marginBottom:28}}>
            {[
              {label:"Total Recipes",value:total,icon:"📖"},
              {label:"Favourites",value:favs,icon:"❤️"},
              {label:"Voice Notes",value:withVoice,icon:"🎙️"},
              {label:"With Photos",value:withPhotos,icon:"📷"},
              {label:"Comments",value:totalComments,icon:"💬"},
              {label:"Adaptations",value:adapted,icon:"🔀"},
              {label:"Ratings Given",value:totalRatings,icon:"⭐"},
            ].map(s=>(
              <div key={s.label} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:"14px 16px",textAlign:"center"}}>
                <div style={{fontSize:22,marginBottom:6}}>{s.icon}</div>
                <div style={{fontFamily:"Playfair Display, serif",fontSize:24,color:C.brown,fontWeight:700}}>{s.value}</div>
                <div style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,marginTop:2}}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Top rated */}
          {topRated.length>0&&(
            <div style={{marginBottom:24}}>
              <h3 style={SH}>⭐ Top Rated</h3>
              {topRated.map(r=>{const avg=(r.ratings.reduce((s,x)=>s+x.stars,0)/r.ratings.length).toFixed(1);return(
                <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                  <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.text}}>{r.name}</span>
                  <span style={{fontFamily:"Playfair Display, serif",fontSize:15,color:C.amber}}>⭐ {avg}</span>
                </div>
              );})}
            </div>
          )}

          {/* Most commented */}
          {mostCommented[0]?.comments?.length>0&&(
            <div style={{marginBottom:24}}>
              <h3 style={SH}>💬 Most Discussed</h3>
              {mostCommented.filter(r=>r.comments?.length>0).map(r=>(
                <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                  <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.text}}>{r.name}</span>
                  <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted}}>{r.comments.length} comment{r.comments.length!==1?"s":""}</span>
                </div>
              ))}
            </div>
          )}

          {/* Most adapted */}
          {mostAdapted.length>0&&(
            <div style={{marginBottom:24}}>
              <h3 style={SH}>🔀 Most Adapted Recipes</h3>
              {mostAdapted.map(r=>(
                <div key={r.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                  <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.text}}>{r.name} <span style={{color:C.muted,fontSize:12}}>by {r.contributor}</span></span>
                  <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.amber}}>{r.adaptCount} version{r.adaptCount!==1?"s":""}</span>
                </div>
              ))}
            </div>
          )}

          {/* Contributors bar chart */}
          {byContrib.length>0&&(
            <div style={{marginBottom:24}}>
              <h3 style={SH}>👨‍🍳 Recipes by Contributor</h3>
              {byContrib.map(({name,count})=>(
                <div key={name} style={{marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                    <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.text}}>{name}</span>
                    <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted}}>{count}</span>
                  </div>
                  <div style={{height:8,background:C.parchment,borderRadius:4,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${(count/maxCount)*100}%`,background:`linear-gradient(to right,${C.amber},${C.sienna})`,borderRadius:4,transition:"width .5s"}}/>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Country distribution */}
          {countryList.length>0&&(
            <div style={{marginBottom:24}}>
              <h3 style={SH}>🌍 Recipes by Country</h3>
              <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
                {countryList.map(([country,count])=>(
                  <span key={country} style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,background:C.card,border:`1px solid ${C.border}`,borderRadius:20,padding:"4px 13px",color:C.brown}}>
                    {country} <span style={{color:C.amber,fontWeight:600}}>{count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Monthly timeline */}
          <div style={{marginBottom:8}}>
            <h3 style={SH}>📅 Recipes Added by Month</h3>
            <div style={{display:"flex",gap:4,alignItems:"flex-end",height:80}}>
              {months.map((m,i)=>(
                <div key={m} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                  <div style={{width:"100%",background:`linear-gradient(to top,${C.amber},${C.sienna})`,borderRadius:"3px 3px 0 0",height:byMonth[i]===0?2:`${(byMonth[i]/maxMonth)*60}px`,opacity:byMonth[i]===0?.2:1,transition:"height .5s"}}/>
                  <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:9,color:C.muted}}>{m}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── PUBLIC GALLERY ────────────────────────────────────────────────────────────
function PublicGallery({recipes,onClose}){
  const public_recipes=recipes.filter(r=>r.isPublic);
  const [search,setSearch]=useState("");
  const filtered=public_recipes.filter(r=>!search||r.name.toLowerCase().includes(search.toLowerCase())||r.contributor?.toLowerCase().includes(search.toLowerCase()));

  return(
    <div style={{position:"fixed",inset:0,background:C.overlay,zIndex:300,overflowY:"auto"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.bg,maxWidth:720,margin:"0 auto",minHeight:"100vh",boxShadow:"0 0 50px rgba(0,0,0,.4)",animation:"fadeIn .25s ease"}}>
        <div style={{background:C.headerBg,padding:"24px 28px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <h2 style={{fontFamily:"Playfair Display, serif",fontSize:26,color:C.parchment,margin:0}}>🌐 Public Gallery</h2>
            <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",color:`${C.parchment}80`,fontSize:14,margin:"4px 0 0"}}>{public_recipes.length} recipe{public_recipes.length!==1?"s":""} shared with the world</p>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:"50%",width:36,height:36,cursor:"pointer",color:"#fff",fontSize:16}}>✕</button>
        </div>
        <div style={{padding:"20px 24px"}}>
          {public_recipes.length===0?(
            <div style={{textAlign:"center",paddingTop:40}}>
              <div style={{fontSize:56,marginBottom:12}}>🔒</div>
              <p style={{fontFamily:"Playfair Display, serif",fontSize:20,color:C.brown,margin:"0 0 8px"}}>No public recipes yet</p>
              <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",fontSize:15,color:C.muted}}>Mark recipes as 🌐 Public on any recipe card to feature them here.</p>
            </div>
          ):(
            <>
              <div style={{background:`${C.blue}10`,border:`1px solid ${C.blue}30`,borderRadius:9,padding:"10px 14px",marginBottom:16,display:"flex",gap:8,alignItems:"flex-start"}}>
                <span style={{fontSize:16}}>ℹ️</span>
                <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.blue,margin:0,lineHeight:1.6}}>These recipes are marked as public. Share this vault's URL with friends and family — they can browse these recipes without signing in.</p>
              </div>
              <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search public recipes…" style={{...IS,marginBottom:16}}/>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:14}}>
                {filtered.map(r=>{
                  const di=DIETARY.find(d=>d.id===r.dietaryType);
                  const cover=r.photos?.[0]||r.imageBase64;
                  return(
                    <div key={r.id} style={{background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden"}}>
                      {cover&&<img src={cover} alt={r.name} style={{width:"100%",height:120,objectFit:"cover"}}/>}
                      <div style={{padding:"12px 14px"}}>
                        <div style={{display:"flex",gap:6,marginBottom:6,flexWrap:"wrap"}}>
                          {di&&<span style={{fontSize:11,fontFamily:"Cormorant Garamond, serif",color:di.color,background:`${di.color}18`,padding:"2px 8px",borderRadius:20}}>{di.emoji} {di.label}</span>}
                          {r.ratings?.length>0&&<span style={{fontSize:11,fontFamily:"Cormorant Garamond, serif",color:C.amber}}>⭐ {(r.ratings.reduce((s,x)=>s+x.stars,0)/r.ratings.length).toFixed(1)}</span>}
                        </div>
                        <h3 style={{fontFamily:"Playfair Display, serif",fontSize:17,color:C.brown,margin:"0 0 4px",lineHeight:1.3}}>{r.name}</h3>
                        {r.description&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,margin:"0 0 6px",lineHeight:1.5,display:"-webkit-box",WebkitLineClamp:2,WebkitBoxOrient:"vertical",overflow:"hidden"}}>{r.description}</p>}
                        <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",fontSize:12,color:C.sienna,margin:0}}>by {r.contributor}</p>
                        {(r.contributorCity||r.contributorCountry)&&<p style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,margin:"2px 0 0"}}>📍 {[r.contributorCity,r.contributorCountry].filter(Boolean).join(", ")}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── WEEKLY DIGEST ────────────────────────────────────────────────────────────
function DigestPreview({recipes,onClose}){
  const oneWeekAgo=new Date();oneWeekAgo.setDate(oneWeekAgo.getDate()-7);
  const newRecipes=recipes.filter(r=>r.createdAt&&new Date(r.createdAt)>oneWeekAgo);
  const topFav=[...recipes].sort((a,b)=>(b.ratings?.length||0)-(a.ratings?.length||0)).slice(0,3);

  const digestText=`🍽️ Rasoi Ghar — Weekly Family Digest
${new Date().toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"})}

📖 NEW THIS WEEK (${newRecipes.length} recipes)
${newRecipes.map(r=>`• ${r.name} — by ${r.contributor}${r.contributorCity?`, ${r.contributorCity}`:""}${r.description?`\n  "${r.description}"`:""}`)
.join("\n") || "No new recipes this week — be the first to add one!"}

❤️ MOST LOVED
${topFav.filter(r=>r.ratings?.length).map(r=>{const avg=(r.ratings.reduce((s,x)=>s+x.stars,0)/r.ratings.length).toFixed(1);return`• ${r.name} (⭐${avg}) — by ${r.contributor}`;}).join("\n")||"Rate some recipes to see them here!"}

📊 VAULT STATS
Total recipes: ${recipes.length}
Contributors: ${new Set(recipes.map(r=>r.contributor).filter(Boolean)).size}
Comments this week: ${recipes.reduce((s,r)=>s+(r.comments||[]).filter(c=>c.date&&new Date(c.date)>oneWeekAgo).length,0)}

— Rasoi Ghar 📜 · Preserving grandma's secrets for generations`;

  return(
    <div style={{position:"fixed",inset:0,background:C.overlay,zIndex:300,overflowY:"auto"}} onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:C.bg,maxWidth:600,margin:"0 auto",minHeight:"100vh",boxShadow:"0 0 50px rgba(0,0,0,.4)",animation:"fadeIn .25s ease"}}>
        <div style={{background:C.headerBg,padding:"24px 28px",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div>
            <h2 style={{fontFamily:"Playfair Display, serif",fontSize:26,color:C.parchment,margin:0}}>📧 Weekly Digest</h2>
            <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",color:`${C.parchment}80`,fontSize:14,margin:"4px 0 0"}}>Copy and send to your family</p>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,.15)",border:"none",borderRadius:"50%",width:36,height:36,cursor:"pointer",color:"#fff",fontSize:16}}>✕</button>
        </div>
        <div style={{padding:"24px 28px"}}>

          {/* New recipes this week */}
          <div style={{marginBottom:20}}>
            <h3 style={SH}>📖 New This Week ({newRecipes.length})</h3>
            {newRecipes.length===0?(
              <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",fontSize:14,color:C.muted}}>No new recipes added in the last 7 days.</p>
            ):newRecipes.map(r=>(
              <div key={r.id} style={{display:"flex",gap:12,alignItems:"flex-start",padding:"10px 0",borderBottom:`1px solid ${C.border}`}}>
                {(r.photos?.[0]||r.imageBase64)&&<img src={r.photos?.[0]||r.imageBase64} alt="" style={{width:48,height:48,objectFit:"cover",borderRadius:6,flexShrink:0}}/>}
                <div>
                  <p style={{fontFamily:"Playfair Display, serif",fontSize:15,color:C.brown,margin:"0 0 2px"}}>{r.name}</p>
                  <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,margin:0}}>by {r.contributor}{r.contributorCity?` · ${r.contributorCity}`:""}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Top rated */}
          {topFav.filter(r=>r.ratings?.length).length>0&&(
            <div style={{marginBottom:20}}>
              <h3 style={SH}>❤️ Family Favourites</h3>
              {topFav.filter(r=>r.ratings?.length).map(r=>{
                const avg=(r.ratings.reduce((s,x)=>s+x.stars,0)/r.ratings.length).toFixed(1);
                return(
                  <div key={r.id} style={{display:"flex",justifyContent:"space-between",padding:"8px 0",borderBottom:`1px solid ${C.border}`}}>
                    <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:15,color:C.text}}>{r.name}</span>
                    <span style={{fontFamily:"Playfair Display, serif",fontSize:14,color:C.amber}}>⭐ {avg}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Copy button */}
          <button onClick={()=>navigator.clipboard.writeText(digestText).then(()=>alert("Digest copied! Paste into WhatsApp, Email, or any messaging app."))}
            style={BP}>
            📋 Copy Digest to Clipboard
          </button>
          <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,textAlign:"center",marginTop:10}}>
            Paste into WhatsApp, Gmail, or any group chat to share with family.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── VAULT INTRO CARD ────────────────────────────────────────────────────────
function VaultIntroCard(){
  const key="vault-intro-dismissed";
  const [dismissed,setDismissed]=useState(()=>{try{return localStorage.getItem(key)==="1";}catch{return false;}});
  const [open,setOpen]=useState(true);

  const dismiss=()=>{
    try{localStorage.setItem(key,"1");}catch{}
    setDismissed(true);
  };

  if(dismissed)return null;

  return(
    <div style={{marginBottom:18,background:C.card,border:`1.5px solid ${C.amber}55`,borderRadius:13,overflow:"hidden",animation:"fadeIn .4s ease"}}>
      {/* Card header — always visible, click to expand/collapse */}
      <button onClick={()=>setOpen(o=>!o)}
        style={{width:"100%",display:"flex",alignItems:"center",justifyContent:"space-between",padding:"13px 18px",background:`linear-gradient(135deg,${C.amber}18,${C.sienna}0a)`,border:"none",cursor:"pointer",textAlign:"left"}}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:20}}>📜</span>
          <div>
            <p style={{fontFamily:"Playfair Display, serif",fontSize:15,color:C.brown,margin:0,fontWeight:700}}>A note from Ritesh Shah, creator of this vault</p>
            <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,margin:0,fontStyle:"italic"}}>Mumbai, India · Tap to {open?"collapse":"read"}</p>
          </div>
        </div>
        <span style={{fontSize:14,color:C.amber,transition:"transform .2s",display:"inline-block",transform:open?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
      </button>

      {/* Collapsible body */}
      {open&&(
        <div style={{padding:"16px 20px 18px",borderTop:`1px solid ${C.amber}30`}}>
          <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15.5,color:C.text,lineHeight:1.85,margin:"0 0 14px"}}>
            Dear family and friends,
          </p>
          <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15.5,color:C.text,lineHeight:1.85,margin:"0 0 14px"}}>
            I built <em>Rasoi Ghar</em> because our family's recipes were scattered across handwritten cards, fading memories, and voices that won't be with us forever. I wanted to give every dish a permanent home — and none of this would have been possible without my better half, <strong>Neeta Shah</strong>, whose patience, passion, and handwritten recipe notes are the very soul of this vault.
          </p>
          <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15.5,color:C.text,lineHeight:1.85,margin:"0 0 14px"}}>
            Every recipe you see here today began as one of Neeta's handwritten notes — carefully written, lovingly cooked, and now preserved here for the generations who will one day cook from them and carry these flavours forward.
          </p>
          <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:15.5,color:C.text,lineHeight:1.85,margin:"0 0 16px"}}>
            But <em>Rasoi Ghar</em> belongs to the whole family. If you have a recipe to share — whether it lives in your memory, in a worn notebook, or on a scrap of paper tucked in a drawer — you are warmly welcome to add it. Record it in your own voice, scan a handwritten card, or simply type it out. Whatever form feels natural to you, this vault will keep it safe forever.
          </p>
          <div style={{display:"flex",alignItems:"center",gap:10,justifyContent:"space-between",flexWrap:"wrap"}}>
            <p style={{fontFamily:"Playfair Display, serif",fontStyle:"italic",fontSize:14,color:C.sienna,margin:0}}>
              — Ritesh Mulchand Shah, Mumbai
            </p>
            <button onClick={dismiss}
              style={{background:"none",border:`1px solid ${C.border}`,borderRadius:7,padding:"5px 14px",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,cursor:"pointer"}}>
              ✓ Got it, don't show again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── PDF EXPORT ───────────────────────────────────────────────────────────────
function exportCookbookPDF(recipes){
  const DM=Object.fromEntries(DIETARY.map(d=>[d.id,d]));
  const OM=Object.fromEntries(OCCASIONS.map(o=>[o.id,o]));
  const CM=Object.fromEntries(CUISINES.map(c=>[c.id,c]));

  const cover=`<div class="page cover-page"><div class="cover-ornament">✦ ✦ ✦</div><h1 class="cover-title">Rasoi Ghar</h1><p class="cover-subtitle">Preserving grandma's secrets for generations</p><div class="cover-divider"></div><p class="cover-meta">${recipes.length} recipes preserved</p><p class="cover-meta">${new Date().toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"})}</p><div class="cover-ornament" style="margin-top:auto">✦ ✦ ✦</div></div>`;

  const index=`<div class="page index-page"><h2 class="section-title">Index</h2><div class="index-divider"></div><table class="index-table"><thead><tr><th>#</th><th>Recipe</th><th>Contributed by</th><th>Type</th></tr></thead><tbody>${recipes.map((r,i)=>{const d=DM[r.dietaryType];return`<tr><td class="idx-num">${i+1}</td><td class="idx-name">${r.name||"Untitled"}</td><td class="idx-contrib">${r.contributor||"—"}</td><td class="idx-type">${d?d.emoji+" "+d.label:"—"}</td></tr>`;}).join("")}</tbody></table></div>`;

  const pages=recipes.map((r,i)=>{
    const d=DM[r.dietaryType];
    const occasions=(r.occasionTags||[]).map(ot=>OM[ot]).filter(Boolean);
    const cuisines=(r.cuisineTags||[]).map(ct=>CM[ct]).filter(Boolean);
    const typeIcon={voice:"🎙️ Voice",scan:"📷 Scanned",manual:"✍️ Typed"}[r.inputType]||"";
    const cover=r.photos?.[0]||r.imageBase64;
    const nutri=r.nutrition;
    return`<div class="page recipe-page">
      <div class="recipe-header"><div class="recipe-num">${i+1}</div><div>
        <div class="recipe-badges">${typeIcon?`<span class="badge">${typeIcon}</span>`:""}${d?`<span class="badge dietary-badge">${d.emoji} ${d.label}</span>`:""}${occasions.map(o=>`<span class="badge occasion-badge">${o.emoji} ${o.label}</span>`).join("")}${cuisines.map(c=>`<span class="badge cuisine-badge">${c.emoji} ${c.label}</span>`).join("")}</div>
        <h2 class="recipe-title">${r.name||"Untitled"}</h2>
        ${r.description?`<p class="recipe-desc">${r.description}</p>`:""}
        ${r.contributor?`<p class="recipe-by">by ${r.contributor}</p>`:""}
        ${r.adaptedFrom?`<p class="recipe-by">🔀 Adapted from ${r.adaptedFrom.name}</p>`:""}
      </div></div>
      ${cover?`<img class="recipe-photo" src="${cover}" alt="${r.name}"/>`:""}
      <div class="recipe-meta">${r.prepTime?`<div class="meta-box"><div class="meta-label">Prep</div><div class="meta-val">${r.prepTime}</div></div>`:""}${r.cookTime?`<div class="meta-box"><div class="meta-label">Cook</div><div class="meta-val">${r.cookTime}</div></div>`:""}${r.servings?`<div class="meta-box"><div class="meta-label">Serves</div><div class="meta-val">${r.servings}</div></div>`:""}</div>
      ${nutri?`<div class="nutri-row">${[["🔥","Cal",nutri.calories],["💪","Protein",nutri.protein],["🍞","Carbs",nutri.carbs],["🧈","Fat",nutri.fat]].filter(([,,v])=>v).map(([e,l,v])=>`<div class="nutri-box"><div class="nutri-label">${e} ${l}</div><div class="nutri-val">${v}</div></div>`).join("")}</div>`:""}
      ${r.ingredients?.length?`<div class="section"><h3 class="section-heading">Ingredients</h3><ul class="ing-list">${r.ingredients.map(i=>`<li>${i}</li>`).join("")}</ul></div>`:""}
      ${r.steps?.length?`<div class="section"><h3 class="section-heading">Method</h3>${r.steps.map((s,si)=>`<div class="step"><span class="step-num">${si+1}</span><p>${s}</p></div>`).join("")}</div>`:""}
      ${r.notes?`<div class="notes-box"><strong>💛 Notes</strong><p>${r.notes}</p></div>`:""}
      ${r.tags?.length?`<div class="tags-row">${r.tags.map(t=>`<span class="tag">${t}</span>`).join("")}</div>`:""}
      <div class="recipe-footer">Added ${new Date(r.createdAt).toLocaleDateString("en-IN",{day:"numeric",month:"long",year:"numeric"})}</div>
    </div>`;
  }).join("");

  const css=`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700&family=Crimson+Pro:ital,wght@0,400;0,600&display=swap');
*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Cormorant Garamond',serif;background:#fff;color:#2D1B0E}
.page{width:100%;min-height:100vh;padding:48px 56px;page-break-after:always;display:flex;flex-direction:column}
@media print{.page{break-after:page}body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
.cover-page{align-items:center;justify-content:center;background:linear-gradient(160deg,#1C3A2A,#0F2018);color:#F0EAD8;gap:16px}
.cover-ornament{font-size:22px;color:#B87010;letter-spacing:10px}.cover-title{font-family:'Playfair Display',serif;font-size:52px;font-weight:700;text-align:center}
.cover-subtitle{font-size:20px;font-style:italic;color:rgba(240,224,192,.75);text-align:center}.cover-divider{width:120px;height:2px;background:#B87010;margin:10px 0}
.cover-meta{font-size:16px;color:rgba(240,224,192,.65)}
.index-page{background:#FDF6E3}.section-title{font-family:'Playfair Display',serif;font-size:32px;color:#4A2010;margin-bottom:12px}
.index-divider{height:2px;background:#D4B080;margin-bottom:24px}.index-table{width:100%;border-collapse:collapse}
.index-table th{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#7A5840;border-bottom:1px solid #D4B080;padding:8px 10px;text-align:left}
.index-table td{padding:10px;border-bottom:1px solid #F0E0C0;font-size:15px}.idx-num{color:#B87010;font-weight:600;width:32px}
.idx-name{font-family:'Playfair Display',serif;font-weight:600;color:#4A2010}.idx-contrib{color:#8B4520;font-style:italic}
.recipe-page{background:#FDF6E3}.recipe-header{display:flex;gap:18px;align-items:flex-start;margin-bottom:18px}
.recipe-num{min-width:44px;height:44px;background:linear-gradient(135deg,#B87010,#8B4520);border-radius:50%;display:flex;align-items:center;justify-content:center;color:#fff;font-family:'Playfair Display',serif;font-size:18px;font-weight:700;flex-shrink:0;margin-top:4px}
.recipe-badges{display:flex;gap:8px;flex-wrap:wrap;margin-bottom:6px}.badge{font-size:11px;background:#E8D5A8;color:#8B4520;padding:2px 10px;border-radius:20px}
.dietary-badge{background:#E8F0E0;color:#3D7A55}.occasion-badge{background:rgba(184,112,16,.15);color:#B87010}.cuisine-badge{background:rgba(45,96,128,.12);color:#2D6080}
.recipe-title{font-family:'Playfair Display',serif;font-size:28px;color:#4A2010;line-height:1.2;margin-bottom:6px}
.recipe-desc{font-size:15px;font-style:italic;color:#7A5840;line-height:1.6;margin-bottom:6px}.recipe-by{font-size:13px;color:#8B4520;font-style:italic}
.recipe-photo{width:100%;max-height:220px;object-fit:cover;border-radius:9px;margin-bottom:18px}
.recipe-meta{display:flex;gap:12px;margin-bottom:16px}.meta-box{background:#F0E0C0;border:1px solid #D4B080;border-radius:8px;padding:7px 16px;text-align:center}
.meta-label{font-size:10px;text-transform:uppercase;letter-spacing:.8px;color:#7A5840}.meta-val{font-family:'Playfair Display',serif;font-size:15px;color:#4A2010;margin-top:2px}
.nutri-row{display:flex;gap:10px;margin-bottom:16px;flex-wrap:wrap}.nutri-box{background:#F8F0E0;border:1px solid #D4B080;border-radius:8px;padding:6px 14px;text-align:center}
.nutri-label{font-size:10px;text-transform:uppercase;letter-spacing:.8px;color:#7A5840}.nutri-val{font-family:'Playfair Display',serif;font-size:14px;color:#4A2010;margin-top:2px}
.section{margin-bottom:20px}.section-heading{font-family:'Playfair Display',serif;font-size:17px;color:#4A2010;border-bottom:1px solid #D4B080;padding-bottom:5px;margin-bottom:10px}
.ing-list{list-style:none;padding:0}.ing-list li{font-size:15px;padding:7px 0;border-bottom:1px solid #F0E0C0}
.ing-list li::before{content:"◆ ";color:#B87010;font-size:10px}
.step{display:flex;gap:13px;margin-bottom:13px}.step-num{min-width:28px;height:28px;border-radius:50%;background:linear-gradient(135deg,#B87010,#8B4520);display:flex;align-items:center;justify-content:center;color:#fff;font-family:'Playfair Display',serif;font-size:13px;font-weight:700;flex-shrink:0;margin-top:2px}
.step p{font-size:15px;line-height:1.75;margin:0;padding-top:4px}
.notes-box{background:rgba(184,112,16,.1);border:1px solid rgba(184,112,16,.35);border-radius:9px;padding:13px 16px;margin-bottom:16px}
.notes-box strong{font-size:14px;color:#B87010;display:block;margin-bottom:6px}.notes-box p{font-size:14.5px;font-style:italic;line-height:1.75}
.tags-row{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:16px}.tag{font-size:12px;background:#E8D5A8;color:#8B4520;padding:3px 12px;border-radius:20px;border:1px solid #D4B080}
.recipe-footer{font-size:12px;color:#D4B080;text-align:center;margin-top:auto;padding-top:16px}`;

  // Embed auto-print script inside the HTML itself — fires when the page loads in the new tab
  const htmlFull = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Rasoi Ghar — Cookbook</title>
  <style>${css}</style>
</head>
<body>
  ${cover}${index}${pages}
  <script>
    window.addEventListener('load', function() {
      setTimeout(function() { window.print(); }, 800);
    });
  <\/script>
</body>
</html>`;

  // Use a Blob URL — works on Vercel/hosted sites where window.open("","_blank") is blocked
  const blob = new Blob([htmlFull], { type: "text/html;charset=utf-8" });
  const url  = URL.createObjectURL(blob);
  const tab  = window.open(url, "_blank");
  if (!tab) {
    alert("Pop-ups are blocked. Please allow pop-ups for this site in your browser, then try again.");
  }
  // Revoke after 2 minutes to free memory
  setTimeout(() => URL.revokeObjectURL(url), 120000);
}

// ─── MAIN APP ────────────────────────────────────────────────────────────────
export default function GrandmasRecipeVault(){
  const [recipes,setRecipes]=useState([]);
  const [loading,setLoading]=useState(true);
  const [search,setSearch]=useState("");
  const [activeFilter,setActiveFilter]=useState("all");
  const [activeContributor,setActiveContributor]=useState("all");
  const [activeCuisine,setActiveCuisine]=useState("all");
  const [showFavourites,setShowFavourites]=useState(false);
  const [showAdd,setShowAdd]=useState(false);
  const [showExportMenu,setShowExportMenu]=useState(false);
  const [showChefs,setShowChefs]=useState(false);
  const [showMealPlanner,setShowMealPlanner]=useState(false);
  const [showAnalytics,setShowAnalytics]=useState(false);
  const [showGallery,setShowGallery]=useState(false);
  const [showDigest,setShowDigest]=useState(false);
  const [selected,setSelected]=useState(null);
  const [adaptFrom,setAdaptFrom]=useState(null);
  const [identity,setIdentity]=useState(null); // null = not yet identified
  const [showIdentity,setShowIdentity]=useState(false); // switch identity

  useEffect(()=>{
    injectFonts();
    loadAllRecipes().then(r=>{setRecipes(r);setLoading(false);});
    const saved=loadIdentity();
    if(saved)setIdentity(saved);
    // else identity stays null → IdentityModal will show
  },[]);

  const handleSaved=r=>setRecipes(prev=>[r,...prev]);
  const handleDelete=async()=>{await removeRecipe(selected.id);setRecipes(prev=>prev.filter(r=>r.id!==selected.id));setSelected(null);};
  const handleUpdate=async updated=>{await updateRecipe(updated);setRecipes(prev=>prev.map(r=>r.id===updated.id?updated:r));setSelected(s=>s?.id===updated.id?updated:s);};
  const handleToggleFavourite=async recipe=>{const u={...recipe,isFavourite:!recipe.isFavourite};await updateRecipe(u);setRecipes(prev=>prev.map(r=>r.id===recipe.id?u:r));};
  const handleAdapt=recipe=>{setSelected(null);setAdaptFrom(recipe);setShowAdd(true);};

  const contributors=["all",...Array.from(new Set(recipes.map(r=>r.contributor).filter(Boolean))).sort()];
  const usedCuisines=["all",...Array.from(new Set(recipes.flatMap(r=>r.cuisineTags||[]))).sort()];

  const filtered=recipes.filter(r=>{
    if(showFavourites&&!r.isFavourite)return false;
    if(activeFilter!=="all"&&r.dietaryType!==activeFilter)return false;
    if(activeContributor!=="all"&&r.contributor!==activeContributor)return false;
    if(activeCuisine!=="all"&&!(r.cuisineTags||[]).includes(activeCuisine))return false;
    if(!search)return true;
    const q=search.toLowerCase();
    return r.name?.toLowerCase().includes(q)||r.contributor?.toLowerCase().includes(q)
      ||r.tags?.some(t=>t.toLowerCase().includes(q))||r.description?.toLowerCase().includes(q)
      ||r.ingredients?.some(i=>i.toLowerCase().includes(q))||(r.occasionTags||[]).some(t=>t.toLowerCase().includes(q))
      ||(r.cuisineTags||[]).some(t=>t.toLowerCase().includes(q));
  });

  return(
    <div style={{minHeight:"100vh",background:C.bg}}>
      {/* Header */}
      <header style={{background:C.headerBg,padding:"18px 20px 14px",position:"relative"}}>
        {/* Decorative layers — in their own clipped div so the dropdown is NOT clipped */}
        <div style={{position:"absolute",inset:0,overflow:"hidden",pointerEvents:"none",borderRadius:"inherit"}}>
          <div style={{position:"absolute",inset:0,opacity:.05,backgroundImage:C.headerPattern,backgroundSize:"22px 22px"}}/>
          <div style={{position:"absolute",bottom:-30,left:"50%",transform:"translateX(-50%)",width:"70%",height:60,background:`radial-gradient(ellipse, ${C.amber}30 0%, transparent 70%)`}}/>
        </div>

        <div style={{position:"relative",display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
          <div>
            {/* App title */}
            <h1 style={{fontFamily:"Playfair Display, serif",fontSize:28,fontWeight:900,color:"#F5E6C8",margin:"0 0 12px",letterSpacing:.8,lineHeight:1.1}}>
              📜 Rasoi Ghar
            </h1>

            {/* Tagline — elegant ruled line, no pill, no border box */}
            <div style={{display:"flex",alignItems:"center",gap:10}}>
              <div style={{height:1,flex:"0 0 28px",background:`linear-gradient(to right, transparent, ${C.amber})`}}/>
              <p style={{
                fontFamily:"'Cormorant Garamond', serif",
                fontStyle:"italic",
                fontWeight:600,
                fontSize:14,
                color:"#E8D5A0",
                margin:0,
                letterSpacing:1.2,
                whiteSpace:"nowrap",
              }}>
                Preserving grandma's secrets for generations
              </p>
              <div style={{height:1,flex:"0 0 28px",background:`linear-gradient(to left, transparent, ${C.amber})`}}/>
            </div>
          </div>
          <div style={{display:"flex",gap:7,alignItems:"center",flexWrap:"wrap",marginTop:4}}>
            {[
              {icon:"❤️",label:"Favourites",active:showFavourites,onClick:()=>setShowFavourites(f=>!f),color:C.favourite},
              {icon:"👨‍🍳",label:"Chefs",active:showChefs,onClick:()=>setShowChefs(true),color:C.amber},
              {icon:"🗓️",label:"Planner",active:showMealPlanner,onClick:()=>setShowMealPlanner(true),color:C.blue},
              {icon:"🌐",label:"Gallery",active:showGallery,onClick:()=>setShowGallery(true),color:C.blue},
              ...(identity?.isOwner?[
                {icon:"📊",label:"Analytics",active:showAnalytics,onClick:()=>setShowAnalytics(true),color:C.green},
                {icon:"📧",label:"Digest",active:showDigest,onClick:()=>setShowDigest(true),color:C.green},
              ]:[]),
            ].map(b=>(
              <button key={b.label} onClick={b.onClick}
                style={{background:b.active?b.color:"rgba(181,135,58,0.18)",color:"#F5E6C8",border:`1px solid ${b.active?"transparent":"rgba(181,135,58,0.35)"}`,borderRadius:9,padding:"7px 11px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,transition:"all .15s"}}>
                <span style={{fontSize:16}}>{b.icon}</span>
                <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:10}}>{b.label}</span>
              </button>
            ))}
          <div style={{position:"relative"}}>
            <button onClick={()=>setShowExportMenu(m=>!m)}
              style={{background:showExportMenu?`${C.amber}50`:"rgba(181,135,58,0.18)",color:"#F5E6C8",border:`1px solid rgba(181,135,58,0.35)`,borderRadius:9,padding:"7px 11px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2}}>
              <span style={{fontSize:16}}>📄</span>
              <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:10}}>Export PDF</span>
            </button>
            {showExportMenu&&(
              <div style={{position:"absolute",top:"calc(100% + 8px)",right:0,background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:8,zIndex:9999,minWidth:220,boxShadow:"0 12px 40px rgba(0,0,0,.25)"}} onClick={()=>setShowExportMenu(false)}>
                <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:12,color:C.muted,textTransform:"uppercase",letterSpacing:.8,padding:"4px 10px 8px"}}>Export Cookbook</p>
                {[
                  {label:`📖 All Recipes (${recipes.length})`,fn:()=>exportCookbookPDF(recipes)},
                  {label:`❤️ Favourites (${recipes.filter(r=>r.isFavourite).length})`,fn:()=>exportCookbookPDF(recipes.filter(r=>r.isFavourite))},
                ].map(item=>(
                  <button key={item.label} onClick={item.fn} style={{width:"100%",textAlign:"left",padding:"9px 12px",background:"none",border:"none",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.text,borderRadius:7,display:"block"}}
                    onMouseEnter={e=>e.currentTarget.style.background=C.parchment} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    {item.label}
                  </button>
                ))}
                {DIETARY.filter(d=>d.id!=="all").map(d=>{const count=recipes.filter(r=>r.dietaryType===d.id).length;return count>0?(
                  <button key={d.id} onClick={()=>exportCookbookPDF(recipes.filter(r=>r.dietaryType===d.id))} style={{width:"100%",textAlign:"left",padding:"9px 12px",background:"none",border:"none",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.text,borderRadius:7,display:"block"}}
                    onMouseEnter={e=>e.currentTarget.style.background=C.parchment} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    {d.emoji} {d.label} ({count})
                  </button>
                ):null;})}
                {contributors.filter(c=>c!=="all").map(c=>{const count=recipes.filter(r=>r.contributor===c).length;return(
                  <button key={c} onClick={()=>exportCookbookPDF(recipes.filter(r=>r.contributor===c))} style={{width:"100%",textAlign:"left",padding:"9px 12px",background:"none",border:"none",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:14,color:C.text,borderRadius:7,display:"block"}}
                    onMouseEnter={e=>e.currentTarget.style.background=C.parchment} onMouseLeave={e=>e.currentTarget.style.background="none"}>
                    👤 {c}'s Recipes ({count})
                  </button>
                );})}
              </div>
            )}
          </div>
          <button onClick={()=>{setAdaptFrom(null);setShowAdd(true);}}
            style={{background:`linear-gradient(135deg,${C.amber},${C.sienna})`,color:"#fff",border:"none",borderRadius:9,padding:"7px 11px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,boxShadow:"0 3px 12px rgba(0,0,0,.25)"}}>
            <span style={{fontSize:16}}>➕</span>
            <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:10}}>Add Recipe</span>
          </button>

          {/* Identity badge */}
          {identity&&(
            <button onClick={()=>setShowIdentity(true)}
              title="Switch identity"
              style={{background:"rgba(181,135,58,0.15)",border:`1.5px solid ${identity.isOwner?"rgba(181,135,58,0.7)":"rgba(181,135,58,0.3)"}`,borderRadius:9,padding:"7px 11px",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,minWidth:58}}>
              <span style={{fontSize:16}}>{identity.isOwner?"👑":"👤"}</span>
              <span style={{fontFamily:"Cormorant Garamond, serif",fontSize:10,color:identity.isOwner?C.amber:"#F5E6C8",maxWidth:70,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{identity.isOwner?"Owner":identity.name}</span>
            </button>
          )}
        </div>
        </div>
      </header>

      {/* Supabase warning */}
      {!SUPABASE_OK&&<div style={{background:"#FFF3CD",borderBottom:"1px solid #FBBF24",padding:"10px 24px",display:"flex",alignItems:"center",gap:10}}><span style={{fontSize:18}}>⚠️</span><p style={{fontFamily:"Cormorant Garamond, serif",fontSize:14,color:"#92400E",margin:0}}><strong>Cross-device sync not set up.</strong> Recipes save to this browser only. Add your Supabase credentials to sync across all devices.</p></div>}

      {/* 🔔 On This Day */}
      {(()=>{
        const today=new Date();
        const memories=recipes.filter(r=>{
          if(!r.createdAt)return false;
          const d=new Date(r.createdAt);
          return d.getDate()===today.getDate()&&d.getMonth()===today.getMonth()&&d.getFullYear()<today.getFullYear();
        });
        if(memories.length===0)return null;
        return(
          <div style={{background:`linear-gradient(135deg,${C.amber}18,${C.sienna}12)`,borderBottom:`1px solid ${C.amber}40`,padding:"10px 24px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{fontSize:20}}>🔔</span>
            <div>
              <p style={{fontFamily:"Playfair Display, serif",fontSize:14,color:C.brown,margin:0,fontWeight:600}}>On This Day</p>
              <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted,margin:0}}>
                {memories.map((r,i)=>(
                  <span key={r.id}>
                    <button onClick={()=>setSelected(r)} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.sienna,textDecoration:"underline",padding:0}}>{r.name}</button>
                    {" "}was added {new Date().getFullYear()-new Date(r.createdAt).getFullYear()} year{new Date().getFullYear()-new Date(r.createdAt).getFullYear()!==1?"s":""} ago by {r.contributor}
                    {i<memories.length-1?" · ":""}
                  </span>
                ))}
              </p>
            </div>
          </div>
        );
      })()}

      {/* 🎂 Occasion Reminder */}
      {(()=>{
        const month=new Date().getMonth()+1;
        const upcoming=OCCASIONS.filter(o=>{
          const months=OCCASION_MONTHS[o.id];
          return months&&months.includes(month);
        });
        if(upcoming.length===0)return null;
        const tagged=recipes.filter(r=>r.occasionTags?.some(t=>upcoming.some(o=>o.id===t)));
        if(tagged.length===0)return null;
        return(
          <div style={{background:`${C.green}10`,borderBottom:`1px solid ${C.green}30`,padding:"10px 24px",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
            <span style={{fontSize:20}}>{upcoming[0].emoji}</span>
            <p style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.green,margin:0}}>
              <strong>{upcoming.map(o=>o.label).join(" & ")}</strong> is coming up —{" "}
              <button onClick={()=>setActiveFilter("all")} style={{background:"none",border:"none",cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.green,textDecoration:"underline",padding:0}}>
                {tagged.length} recipe{tagged.length!==1?"s":""} in your vault for this occasion
              </button>
            </p>
          </div>
        );
      })()}

      {/* Stats */}
      {recipes.length>0&&<div style={{background:C.parchment,borderBottom:`1px solid ${C.border}`,padding:"8px 24px",display:"flex",gap:20,flexWrap:"wrap"}}>
        {[`📖 ${recipes.length} recipes`,`❤️ ${recipes.filter(r=>r.isFavourite).length} favourites`,`🎙️ ${recipes.filter(r=>r.audioBase64).length} voice notes`,`📷 ${recipes.filter(r=>r.imageBase64||r.photos?.length).length} with photos`,`🎥 ${recipes.filter(r=>r.videoUrl||r.videoFile).length} with video`,`💬 ${recipes.reduce((s,r)=>s+(r.comments?.length||0),0)} comments`].map(s=><span key={s} style={{fontFamily:"Cormorant Garamond, serif",fontSize:13,color:C.muted}}>{s}</span>)}
      </div>}

      {/* Search */}
      <div style={{padding:"18px 24px 8px"}}>
        {/* ── Vault Creator Intro Card ── */}
        <VaultIntroCard/>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍  Search recipes, contributors, ingredients, occasions, cuisines…" style={{...IS,fontSize:15}}/>
      </div>

      {/* Dietary filters */}
      <div style={{padding:"8px 24px",overflowX:"auto",display:"flex",gap:8,scrollbarWidth:"none"}}>
        {DIETARY.map(d=>{const active=activeFilter===d.id;const count=d.id==="all"?recipes.length:recipes.filter(r=>r.dietaryType===d.id).length;return(
          <button key={d.id} onClick={()=>setActiveFilter(d.id)} style={{flexShrink:0,padding:"6px 13px",borderRadius:24,border:`1.5px solid ${active?d.color:C.border}`,background:active?`${d.color}22`:C.card,cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:active?d.color:C.muted,fontWeight:active?600:400,whiteSpace:"nowrap",transition:"all .15s"}}>
            {d.emoji} {d.label}{count>0&&<span style={{fontSize:11,opacity:.7,marginLeft:3}}>({count})</span>}
          </button>
        );})}
      </div>

      {/* Cuisine filters */}
      {usedCuisines.length>2&&<div style={{padding:"4px 24px 8px",overflowX:"auto",display:"flex",gap:8,scrollbarWidth:"none"}}>
        {usedCuisines.map(id=>{const cu=CUISINES.find(c=>c.id===id);const active=activeCuisine===id;const count=id==="all"?recipes.length:recipes.filter(r=>(r.cuisineTags||[]).includes(id)).length;return(
          <button key={id} onClick={()=>setActiveCuisine(id)} style={{flexShrink:0,padding:"6px 13px",borderRadius:24,border:`1.5px solid ${active?C.blue:C.border}`,background:active?`${C.blue}18`:C.card,cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:active?C.blue:C.muted,fontWeight:active?600:400,whiteSpace:"nowrap",transition:"all .15s"}}>
            {id==="all"?"🌍 All Cuisines":`${cu?.emoji||""} ${cu?.label||id}`}{count>0&&<span style={{fontSize:11,opacity:.7,marginLeft:3}}>({count})</span>}
          </button>
        );})}
      </div>}

      {/* Contributor filters */}
      {contributors.length>2&&<div style={{padding:"4px 24px 12px",overflowX:"auto",display:"flex",gap:8,scrollbarWidth:"none"}}>
        {contributors.map(c=>{const active=activeContributor===c;const count=c==="all"?recipes.length:recipes.filter(r=>r.contributor===c).length;return(
          <button key={c} onClick={()=>setActiveContributor(c)} style={{flexShrink:0,padding:"6px 13px",borderRadius:24,border:`1.5px solid ${active?C.sienna:C.border}`,background:active?`${C.sienna}18`:C.card,cursor:"pointer",fontFamily:"Cormorant Garamond, serif",fontSize:13,color:active?C.sienna:C.muted,fontWeight:active?600:400,whiteSpace:"nowrap",transition:"all .15s"}}>
            {c==="all"?"👥 All Contributors":`👤 ${c}`}{count>0&&<span style={{fontSize:11,opacity:.7,marginLeft:4}}>({count})</span>}
          </button>
        );})}
      </div>}

      {/* Grid */}
      <main style={{padding:"12px 24px 40px"}}>
        {loading?<div style={{paddingTop:60}}><Spinner label="Loading the vault…"/></div>
        :filtered.length===0?(
          <div style={{textAlign:"center",paddingTop:70,animation:"fadeIn .4s ease"}}>
            <div style={{fontSize:64,marginBottom:16}}>{search||activeFilter!=="all"||showFavourites?"🔍":"📖"}</div>
            <p style={{fontFamily:"Playfair Display, serif",fontSize:24,color:C.brown,margin:"0 0 8px"}}>{showFavourites?"No favourites yet":search||activeFilter!=="all"?"No recipes found":"The vault is empty"}</p>
            <p style={{fontFamily:"Cormorant Garamond, serif",fontStyle:"italic",fontSize:16,color:C.muted}}>{showFavourites?"Tap 🤍 on any recipe to save it as a favourite":search?`No results for "${search}"`:"Be the first to preserve a grandmother's recipe"}</p>
            {!search&&activeFilter==="all"&&!showFavourites&&<button onClick={()=>setShowAdd(true)} style={{...BP,width:"auto",padding:"13px 32px",marginTop:20,fontSize:17}}>+ Preserve First Recipe</button>}
          </div>
        ):(
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(275px,1fr))",gap:16}}>
            {filtered.map(r=><RecipeCard key={r.id} recipe={r} onClick={()=>setSelected(r)} onToggleFavourite={handleToggleFavourite}/>)}
          </div>
        )}
      </main>

      {showAdd&&<AddRecipeModal onClose={()=>{setShowAdd(false);setAdaptFrom(null);}} onSaved={handleSaved} adaptFrom={adaptFrom}/>}
      {selected&&<RecipeDetail recipe={selected} onClose={()=>setSelected(null)} onDelete={handleDelete} onUpdate={handleUpdate} onAdapt={handleAdapt} allRecipes={recipes} identity={identity}/>}
      {showChefs&&<ChefProfiles recipes={recipes} identity={identity} onClose={()=>setShowChefs(false)} onSelectContributor={c=>{setActiveContributor(c);setShowChefs(false);}}/>}
      {showMealPlanner&&<MealPlanner recipes={recipes} onClose={()=>setShowMealPlanner(false)}/>}
      {showAnalytics&&<AnalyticsDashboard recipes={recipes} onClose={()=>setShowAnalytics(false)}/>}
      {showGallery&&<PublicGallery recipes={recipes} onClose={()=>setShowGallery(false)}/>}
      {showDigest&&<DigestPreview recipes={recipes} onClose={()=>setShowDigest(false)}/>}

      {/* Identity modal — shown on first visit or when switching */}
      {(!identity||showIdentity)&&<IdentityModal onDone={id=>{setIdentity(id);setShowIdentity(false);}}/>}
    </div>
  );
}
